import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Autocomplete, Box, Button, InputAdornment, Switch, TextField, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ROUTES } from '../../types/global/routes.types';
import { formatCurrency } from '../../constants/currency';
import { computeCampaignFeeBreakdown, computeExtraCreatorsAtSubscribedRate } from '../../constants/campaignBudgetFees';
import { createOffsiteCampaign, resetCreatingCampaign } from '../../redux/buzzbridge/actions';
import { campaignSampleVideoSignedUrl, campaignSampleVideoUrl } from '../../configurations/api/url';
import { getFileExtension } from '../../utils/AppUtils';
import { store } from '../../redux/store';
import {
    activeBrandDashboardBrandSelector,
    creatingCampaignSelector,
    lastCreatedCampaignIdSelector,
} from '../../redux/buzzbridge/selectors';
import { reducer as offsiteReducer, initialState as offsiteInitial } from '../Dashboard/offsiteCampaignCreator.reducer';
import { DraggableFieldList } from '../../components/Common/DraggableFieldList';
import { RichTextEditor } from '../../components/Common/RichTextEditor';
import { DeliverableList } from '../../components/Campaign/DeliverableList';
import { DeliverableRoundBuilder } from '../../components/Campaign/DeliverableRoundBuilder';
import { CustomDatePicker } from '../../components/DatePicker/CustomDatePicker';
import type { Colors } from '../../constants/theme';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_MUTED,
    BRAND_LUXE_SECONDARY,
    BRAND_SURFACE_CONTAINER_LOW,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER_HIGHEST,
} from './brandDashboardTokens';
import CampaignFeatureImagePicker from './campaignDetail/CampaignFeatureImagePicker';
import { RichMediaShareSizeHint } from '../../components/Common/RichMediaShareSizeHint';
import CampaignAssetsEditor, { type AssetEntry } from './campaignDetail/CampaignAssetsEditor';
import { ClippingCampaignToggle } from './ClippingCampaignToggle';
import { AppInstallCampaignToggle } from './AppInstallCampaignToggle';
import type { IAppInstall } from '../Dashboard/offsiteCampaignCreator.reducer';
import { PremiumCampaignToggle, type PremiumRequirement } from './PremiumCampaignToggle';
import { WorldwideCampaignBlock } from './WorldwideCampaignBlock';
import { BrandCampaignStepper } from './BrandCampaignStepper';
import { BrandCampaignStepFooter } from './BrandCampaignStepFooter';
import { showToastMessage } from '../../utils/AppUtils';
import { CommissionCampaignFields } from './CommissionCampaignFields';
import {
    commissionBudgetMajorUnits,
    commissionWizardFooterSummary,
    emptyCommissionConfig,
    sanitizeCommissionForPayload,
    resolveCommissionDisplayPoolCents,
    formatCommissionPoolDollars,
} from '../../utils/commissionCampaign';
import type { ICampaignCommissionConfig } from '../../redux/buzzbridge/types';
import {
    clearUgcCampaignWizardDraft,
    formatUgcDraftSavedAt,
    loadUgcCampaignWizardDraft,
    saveUgcCampaignWizardDraft,
    type UgcCampaignFormDraft,
} from './ugcCampaignDraftStorage';
import { storeWalletFundReturnUrl } from './walletFundRailUtils';
import { plainTextFromHtml } from './contestWizard/richTextUtils';
import {
    NICHE_MAIN_ALPHABETICAL,
    MAIN_CATEGORY_QUICK_PICK,
    REWARD_OPTIONS,
    TOTAL_STEPS,
    STEP_LABELS,
    getFieldSx,
    getUgcBudgetFieldSx,
    parseBudgetInput,
    ugcBudgetDatePickerSx,
    autoFillStep0,
    autoFillStep1,
    autoFillStep2,
    DEFAULT_PRODUCTION_REQUIREMENTS,
    getSubcategoriesForMain,
    isValidSubForMain,
    serializeCampaignLocation,
    isValidClippingAssetUrl,
    buildNicheOptionsAlphabetical,
    iconForNicheMain,
} from './ugcCampaignFormHelpers';
import { useContentCategories } from '../../hooks/useContentCategories';
import { getSubcategoriesForMainFromCatalog } from '../../utils/contentCategoriesApi';
import {
    activeInfluencerTiersSelector,
    buzzBridgeConfigSelector,
    campaignPlatformsSelector,
    ugcBudgetConfigSelector,
    ugcCampaignFeeConfigSelector,
} from '../../selectors/selectors';
import { areAllPlatformAutoLinksEnabled, formatDisabledAutoLinkPlatforms } from '../../utils/brandSocialAutoLink';
import {
    hasActiveCampaignHostPlanSelector,
    campaignHostFetchingMineSelector,
    campaignHostCreationAccessSelector,
    // campaignHostPackagesSelector,
    // campaignHostLtdPromoActiveSelector,
    campaignHostPricingSelector,
} from '../../redux/campaignHostSubscription/selectors';
import {
    fetchCampaignHostCreationAccess,
    fetchCampaignHostPricing,
} from '../../redux/campaignHostSubscription/actions';
import {
    clampCreatorsToPlanLimit,
    creationAccessBlocksCreate,
    formatCreationAccessMaxCreatorsLabel,
    getCreationAccessMaxCreators,
    getCreationBillingCopy,
    isOverCreatorPlanLimit,
} from '../../utils/campaignHostCreationEntitlements';
import type { ICampaignHostPackage } from '../../types/global/campaignHostSubscription.types';
import { BrandCampaignSubscriptionGuard } from './subscription/BrandCampaignSubscriptionGuard';
import { CampaignHostTierPickerModal } from './subscription/CampaignHostTierPickerModal';
import { CampaignHostCheckoutDialog } from './subscription/CampaignHostCheckoutDialog';
import { useCampaignHostCheckout } from './subscription/useCampaignHostCheckout';
import { UgcCampaignHostSubscribeBanner } from './UgcCampaignHostSubscribeBanner';
import { filterEnabledSelectedPlatforms, isPlatformSelectable } from '../../utils/ugcCampaignConfig';
import {
    UgcFormFieldCard,
    UgcInfluencerTierCards,
    UgcPlatformChips,
    UgcRewardTypeChips,
    UgcTargetingConfigPanel,
    UgcTargetingSection,
    UgcTargetingSectionLabel,
} from './ugcCampaignTargetingUi';
import { PlacesAutocompleteField } from '../../components/Common/PlacesAutocompleteField';
import type { PlacesSuggestionPalette } from '../../components/Common/PlacesAutocompleteField';
import { useGoogleMapsLoader } from '../../hook/useMapLoader';
import type { ILocation } from '../../types/global/helper';

const UGC_DRAFT_AUTOSAVE_MS = 10_000;
const SECONDARY_CONTAINER = '#006c50';

async function uploadSampleVideos(campaignId: string, files: File[]): Promise<void> {
    const token: string = (store.getState() as any).authentication?.token ?? '';
    const authHeader = token ? `Bearer ${token}` : '';

    for (const file of files) {
        try {
            const ext = getFileExtension(file);
            const signedRes = await fetch(campaignSampleVideoSignedUrl(campaignId, ext), {
                method: 'GET',
                headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
            });
            if (!signedRes.ok) continue;
            const signedJson = await signedRes.json();
            const signedUrl: string = signedJson?.data?.url;
            const videoKey: string = signedJson?.data?.videoKey;
            if (!signedUrl || !videoKey) continue;

            await fetch(signedUrl, {
                method: 'PUT',
                body: file,
                headers: { 'Content-Type': file.type },
            });

            await fetch(campaignSampleVideoUrl(campaignId), {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: authHeader },
                body: JSON.stringify({ url: videoKey }),
            });
        } catch {
            // non-fatal — skip this video, continue with others
        }
    }
}

export interface UgcCuratedFilterSnapshot {
    country: string;
    world_wide: boolean;
    category: string;
    sub_category: string;
}

export interface BrandNewUGCCampaignFormProps {
    themeColors: Colors;
    invitedIds?: Set<string>;
    onCuratedFilterChange?: (snapshot: UgcCuratedFilterSnapshot) => void;
}

/** Prefer a known country name from an address string (last comma segment / substring). */
export function countryHintFromLocationAddress(address?: string): string {
    const raw = String(address || '').trim();
    if (!raw) return '';
    const parts = raw
        .split(',')
        .map(p => p.trim())
        .filter(Boolean);
    if (!parts.length) return '';
    const last = parts[parts.length - 1];
    // Drop trailing postal codes / short tokens that aren't country names.
    if (last.length >= 3 && !/^\d+$/.test(last)) return last;
    if (parts.length >= 2) return parts[parts.length - 2];
    return last;
}

export function BrandNewUGCCampaignForm({
    themeColors: t,
    invitedIds,
    onCuratedFilterChange,
}: BrandNewUGCCampaignFormProps) {
    const fieldSx = getFieldSx(t);
    const budgetFieldSx = useMemo(() => getUgcBudgetFieldSx(t), [t]);
    const navigate = useNavigate();
    const reduxDispatch = useDispatch();
    const activeBrand = useSelector(activeBrandDashboardBrandSelector);
    const creating = useSelector(creatingCampaignSelector);
    const lastCreatedCampaignId = useSelector(lastCreatedCampaignIdSelector);
    const hasPlan = useSelector(hasActiveCampaignHostPlanSelector);
    const fetchingHostPlan = useSelector(campaignHostFetchingMineSelector);
    const creationAccess = useSelector(campaignHostCreationAccessSelector);
    // const packages = useSelector(campaignHostPackagesSelector);
    // const ltdPromoActive = useSelector(campaignHostLtdPromoActiveSelector);
    const hostPricing = useSelector(campaignHostPricingSelector);
    const perCampaignFeeCents = creationAccess?.per_campaign_fee_cents ?? hostPricing?.per_campaign_fee_cents ?? 0;
    const maxCreators = getCreationAccessMaxCreators(creationAccess ?? hostPricing?.creation_access);
    const creatorCapLabel = formatCreationAccessMaxCreatorsLabel(
        creationAccess ?? hostPricing?.creation_access ?? null,
    );
    const billingCopy = getCreationBillingCopy(creationAccess ?? hostPricing?.creation_access ?? null);
    const walletShortfallCents = Number((creationAccess ?? hostPricing?.creation_access)?.shortfall_cents ?? 0);
    const [subGuardOpen, setSubGuardOpen] = useState(false);
    /** True when guard opened for Commission (tab may not have set campaign_type yet). */
    const [subGuardSubscriptionOnly, setSubGuardSubscriptionOnly] = useState(false);
    const [tierPickerOpen, setTierPickerOpen] = useState(false);
    const pendingLaunchAfterSubRef = useRef(false);
    const handleLaunchRef = useRef<(opts?: { bypassWalletShortfall?: boolean }) => void>(() => {});
    /** After create: open fund modal on detail, or save draft and send user to wallet first. */
    const afterCreateNavigateRef = useRef<'detail' | 'wallet'>('detail');
    const submittingRef = useRef(false);
    /** lastCreatedCampaignId before the in-flight create — used to detect failure. */
    const createBaselineIdRef = useRef<string | null>(null);
    const sampleVideoFilesRef = useRef<File[]>([]);

    useEffect(() => {
        reduxDispatch(resetCreatingCampaign());
        reduxDispatch(fetchCampaignHostCreationAccess());
        reduxDispatch(fetchCampaignHostPricing());
    }, [reduxDispatch]);

    useEffect(() => {
        if (!submittingRef.current || creating) return;
        submittingRef.current = false;

        const campaignId = lastCreatedCampaignId;
        const goWalletFirst = afterCreateNavigateRef.current === 'wallet';
        afterCreateNavigateRef.current = 'detail';

        // Create failed (or id unchanged) — stay on the wizard; error toast comes from the saga.
        if (!campaignId || campaignId === createBaselineIdRef.current) {
            return;
        }
        clearUgcCampaignWizardDraft();

        const videos = sampleVideoFilesRef.current;
        const goNext = () => {
            const detailPath = `${ROUTES.BRAND_CAMPAIGN_DETAIL.replace(':campaignId', campaignId)}?fund=1`;
            if (goWalletFirst) {
                storeWalletFundReturnUrl(detailPath);
                showToastMessage(
                    'Campaign saved as draft. Fund your wallet, then you’ll return to finish funding.',
                    'success',
                );
                navigate(ROUTES.BRAND_WALLET);
                return;
            }
            navigate(detailPath);
        };

        if (videos.length > 0) {
            sampleVideoFilesRef.current = [];
            uploadSampleVideos(campaignId, videos).finally(goNext);
        } else {
            goNext();
        }
    }, [creating, lastCreatedCampaignId, navigate]);

    const [step, setStep] = useState(0);
    const [draftHint, setDraftHint] = useState<string | null>(null);
    const lastSavedDraftKeyRef = useRef<string | null>(null);
    const [form, formDispatch] = useReducer(offsiteReducer, {
        ...offsiteInitial,
        reward: 'paid',
        influencer_tier: 'mid',
        platforms: ['instagram'],
        participants_count: 1,
        objectives: [] as string[],
        requirements: [...DEFAULT_PRODUCTION_REQUIREMENTS],
        requirements_seeded: true,
        ideas: [] as string[],
        deliverables: [],
        products: [] as string[],
        description: '',
        title: '',
        brand: null,
        start_date: '',
        end_date: '',
        physical_product: false,
        campaign_type: 'standard',
        sync_publish_date: '',
        is_clipping: false,
        clipping_asset_url: '',
        premium: false,
        premium_requirements: [] as PremiumRequirement[],
        world_wide: false,
        influencer_readme: '',
        commission: emptyCommissionConfig('contest'),
        app_install: { enabled: false } as IAppInstall,
    } as any);

    const [budgetInput, setBudgetInput] = useState('');
    const [publishDate, setPublishDate] = useState('');
    const [participantsInput, setParticipantsInput] = useState('1');
    const [category, setCategory] = useState<string>(NICHE_MAIN_ALPHABETICAL[0]);
    const [subCategory, setSubCategory] = useState<string>('');
    const [exclusiveRights, setExclusiveRights] = useState(true);
    const [featureImageFile, setFeatureImageFile] = useState<File | null>(null);
    const [featureImagePreview, setFeatureImagePreview] = useState<string | null>(null);
    const [appIconFile, setAppIconFile] = useState<File | null>(null);
    const [assets, setAssets] = useState<AssetEntry[]>([]);
    const [sampleVideoFiles, setSampleVideoFiles] = useState<File[]>([]);

    useEffect(() => {
        if (maxCreators == null) return;
        const n = parseInt(participantsInput.trim(), 10);
        if (Number.isFinite(n) && n > maxCreators) {
            setParticipantsInput(String(maxCreators));
        }
    }, [maxCreators, participantsInput]);

    useEffect(() => {
        const loaded = loadUgcCampaignWizardDraft();
        if (!loaded) return;
        const loadedForm = { ...loaded.form } as UgcCampaignFormDraft & { requirements_seeded?: boolean };
        const savedRequirements = Array.isArray(loadedForm.requirements)
            ? loadedForm.requirements.map((line: string) => String(line || '').trim()).filter(Boolean)
            : [];
        if (!loadedForm.requirements_seeded && savedRequirements.length === 0) {
            loadedForm.requirements = [...DEFAULT_PRODUCTION_REQUIREMENTS];
        }
        loadedForm.requirements_seeded = true;
        formDispatch({ type: 'SET_STATE', payload: loadedForm });
        setBudgetInput(loaded.budgetInput);
        const restoredEnd = loaded.form.end_date || loaded.publishDate || '';
        setPublishDate(restoredEnd);
        if (restoredEnd && restoredEnd !== loaded.form.end_date) {
            formDispatch({ type: 'FIELD', field: 'end_date', value: restoredEnd });
        }
        setParticipantsInput(loaded.participantsInput);
        setCategory(loaded.category);
        setSubCategory(loaded.subCategory);
        setExclusiveRights(loaded.exclusiveRights);
        setFeatureImagePreview(loaded.featureImagePreview);
        setAssets(loaded.assets);
        setStep(loaded.step);
        const restoredSnapshot = {
            step: loaded.step,
            form: loadedForm as UgcCampaignFormDraft,
            budgetInput: loaded.budgetInput,
            publishDate: restoredEnd,
            participantsInput: loaded.participantsInput,
            category: loaded.category,
            subCategory: loaded.subCategory,
            exclusiveRights: loaded.exclusiveRights,
            featureImagePreview: loaded.featureImagePreview,
            assets: loaded.assets,
        };
        lastSavedDraftKeyRef.current = JSON.stringify(restoredSnapshot);
        const lastSavedLabel = loaded.savedAt ? ` · last saved ${formatUgcDraftSavedAt(new Date(loaded.savedAt))}` : '';
        setDraftHint(`Draft restored${lastSavedLabel} — re-upload feature image or asset files if needed`);
    }, []);

    useEffect(() => {
        if (!onCuratedFilterChange) return;
        const address =
            (form.location as { address?: string; description?: string } | undefined)?.address ||
            (form.location as { description?: string } | undefined)?.description ||
            '';
        onCuratedFilterChange({
            country: countryHintFromLocationAddress(address),
            world_wide: Boolean(form.world_wide),
            category: category || '',
            sub_category: subCategory || '',
        });
    }, [onCuratedFilterChange, form.location, form.world_wide, category, subCategory]);

    const buildDraftSnapshot = useCallback(
        (nextStep: number) => ({
            step: nextStep,
            form: { ...(form as UgcCampaignFormDraft) },
            budgetInput,
            publishDate,
            participantsInput,
            category,
            subCategory,
            exclusiveRights,
            featureImagePreview,
            assets: assets.map(({ url, description, requirements }) => ({ url, description, requirements })),
        }),
        [
            form,
            budgetInput,
            publishDate,
            participantsInput,
            category,
            subCategory,
            exclusiveRights,
            featureImagePreview,
            assets,
        ],
    );

    const persistDraft = useCallback(
        (nextStep: number, options?: { updateHint?: boolean; force?: boolean }) => {
            const snapshot = buildDraftSnapshot(nextStep);
            const key = JSON.stringify(snapshot);
            if (!options?.force && key === lastSavedDraftKeyRef.current) {
                return false;
            }
            const savedAt = saveUgcCampaignWizardDraft(snapshot);
            if (!savedAt) return false;
            lastSavedDraftKeyRef.current = key;
            if (options?.updateHint !== false) {
                setDraftHint(`Saved at ${formatUgcDraftSavedAt(new Date(savedAt))}`);
            }
            return true;
        },
        [buildDraftSnapshot],
    );

    const handleSaveDraft = useCallback(() => {
        persistDraft(step, { force: true });
        showToastMessage('Campaign draft saved', 'success');
    }, [persistDraft, step]);

    const stepRef = useRef(step);
    stepRef.current = step;
    const persistDraftRef = useRef(persistDraft);
    persistDraftRef.current = persistDraft;

    // Quiet autosave every 10s so creators do not lose in-progress wizard work.
    // Refs keep the interval stable so typing does not reset the timer.
    useEffect(() => {
        if (creating) return undefined;
        const id = window.setInterval(() => {
            if (submittingRef.current) return;
            persistDraftRef.current(stepRef.current);
        }, UGC_DRAFT_AUTOSAVE_MS);
        return () => window.clearInterval(id);
    }, [creating]);

    const update = useCallback((field: string, value: any) => formDispatch({ type: 'FIELD', field, value }), []);
    const selectCategory = useCallback((main: string) => {
        setCategory(main);
        setSubCategory('');
    }, []);

    const f = form as Record<string, any>;
    const isCommission = f.campaign_type === 'commission';
    const isAppInstall = Boolean((f.app_install as IAppInstall)?.enabled);
    const isPercentCommission = isCommission && !isAppInstall;
    const paidOrBoth = !isPercentCommission && (f.reward === 'paid' || f.reward === 'both');
    const giftOrBoth = !isPercentCommission && (f.reward === 'gifted' || f.reward === 'both');

    const ugcFeeConfig = useSelector(ugcCampaignFeeConfigSelector);
    const campaignPlatforms = useSelector(campaignPlatformsSelector);
    const ugcBudget = useSelector(ugcBudgetConfigSelector);
    const activeTiers = useSelector(activeInfluencerTiersSelector);
    const buzzBridgeConfig = useSelector(buzzBridgeConfigSelector);
    const canUseOauthCampaignModes = areAllPlatformAutoLinksEnabled(buzzBridgeConfig);
    const disabledAutoLinkLabel = formatDisabledAutoLinkPlatforms(buzzBridgeConfig);

    const enabledSelectedPlatforms = useMemo(
        () => filterEnabledSelectedPlatforms(f.platforms || [], campaignPlatforms),
        [f.platforms, campaignPlatforms],
    );

    useEffect(() => {
        if (canUseOauthCampaignModes) return;
        if (f.campaign_type === 'sync_upload') {
            formDispatch({ type: 'FIELD', field: 'campaign_type', value: 'standard' });
            formDispatch({ type: 'FIELD', field: 'sync_publish_date', value: '' });
        }
        if (f.premium) {
            formDispatch({ type: 'FIELD', field: 'premium', value: false });
            formDispatch({ type: 'FIELD', field: 'premium_requirements', value: [] });
        }
    }, [canUseOauthCampaignModes, f.campaign_type, f.premium, formDispatch]);

    useEffect(() => {
        if (!activeTiers.length) return;
        if (!activeTiers.some(t => t.id === f.influencer_tier)) {
            update('influencer_tier', activeTiers[0].id);
        }
    }, [activeTiers, f.influencer_tier, update]);

    useEffect(() => {
        if (!campaignPlatforms.length) return;
        const pruned = filterEnabledSelectedPlatforms(f.platforms || [], campaignPlatforms);
        const firstEnabled = campaignPlatforms.find(isPlatformSelectable)?.id;
        if (pruned.length === 0 && firstEnabled) {
            update('platforms', [firstEnabled]);
        } else if (pruned.length !== (f.platforms || []).length) {
            update('platforms', pruned);
        }
    }, [campaignPlatforms, f.platforms, update]);

    const paidCampaignFeeBreakdown = useMemo(() => {
        // App/web CPA funds from working budget — no UGC escrow fee tiers.
        if (!paidOrBoth || isAppInstall) return null;
        const p = Math.max(1, parseInt(participantsInput.trim(), 10) || f.participants_count || 1);
        const deliveryRoundsCount = f.recurrent_deliverables ? (f.deliverable_rounds || []).length || 1 : 1;
        return computeCampaignFeeBreakdown(
            {
                platforms: enabledSelectedPlatforms,
                participantsCount: p,
                influencerTier: f.influencer_tier || activeTiers[0]?.id || 'micro',
                totalBudget: parseBudgetInput(budgetInput),
                hostHasActiveSubscription: hasPlan,
                deliveryRoundsCount,
            },
            ugcFeeConfig,
        );
    }, [
        paidOrBoth,
        isAppInstall,
        enabledSelectedPlatforms,
        f.influencer_tier,
        f.participants_count,
        f.recurrent_deliverables,
        f.deliverable_rounds,
        participantsInput,
        budgetInput,
        ugcFeeConfig,
        activeTiers,
        hasPlan,
    ]);

    // For commission campaigns paidCampaignFeeBreakdown is null, but we still need
    // tier.minimumPerParticipant to validate the base pay field.
    const commissionTierBreakdown = useMemo(() => {
        if (!isCommission) return null;
        return computeCampaignFeeBreakdown(
            {
                platforms: [],
                participantsCount: 1,
                influencerTier: f.influencer_tier || activeTiers[0]?.id || 'micro',
                totalBudget: 0,
                hostHasActiveSubscription: hasPlan,
                deliveryRoundsCount: 1,
            },
            ugcFeeConfig,
        );
    }, [isCommission, f.influencer_tier, activeTiers, hasPlan, ugcFeeConfig]);

    const canAdvanceStep0 =
        Boolean(form.title?.trim()) &&
        Boolean(category) &&
        Boolean(subCategory) &&
        isValidSubForMain(category, subCategory) &&
        (form.campaign_type !== 'sync_upload' || Boolean(String(form.sync_publish_date || '').trim()));
    const canAdvanceStep1 = useMemo(() => {
        const isCommissionStep = f.campaign_type === 'commission';
        const appInstallOn = Boolean((f.app_install as IAppInstall)?.enabled);
        const budget = parseBudgetInput(budgetInput);
        const budgetMeetsPolicy =
            !paidOrBoth ||
            (paidCampaignFeeBreakdown != null && budget + 0.005 >= paidCampaignFeeBreakdown.minimumRequiredBudget);
        // % commission has no escrow budget; app/web CPA needs a working budget.
        const budgetOk = appInstallOn
            ? budget > 0
            : isCommissionStep || !paidOrBoth || (budget > 0 && budgetMeetsPolicy);
        const p = parseInt(participantsInput.trim(), 10);
        const participantsOk = Number.isFinite(p) && p >= 1 && !isOverCreatorPlanLimit(p, maxCreators);
        const invitesOk = !invitedIds?.size || !isOverCreatorPlanLimit(invitedIds.size, maxCreators);
        const platformsOk = (isCommissionStep && appInstallOn) || enabledSelectedPlatforms.length > 0;
        // Recurrent campaigns set delivery dates per-round in Step 2 — no single date required
        const deliveryDateOk = isCommissionStep || Boolean(f.recurrent_deliverables) || Boolean(f.delivery_date);
        const datesOk = Boolean(f.start_date) && Boolean(f.end_date || publishDate) && deliveryDateOk;
        const basePayCents = f.base_payment_per_creator_cents;
        const minPayUsd = paidCampaignFeeBreakdown?.minimumPerParticipant ?? null;
        const basePayOk =
            basePayCents == null || basePayCents === ''
                ? true
                : minPayUsd == null || Number(basePayCents) / 100 >= minPayUsd;
        const commissionConfigOk =
            !isCommissionStep ||
            appInstallOn ||
            Boolean(sanitizeCommissionForPayload(f.commission as ICampaignCommissionConfig));
        return datesOk && budgetOk && platformsOk && participantsOk && invitesOk && basePayOk && commissionConfigOk;
    }, [
        budgetInput,
        publishDate,
        enabledSelectedPlatforms,
        paidOrBoth,
        participantsInput,
        paidCampaignFeeBreakdown,
        maxCreators,
        invitedIds,
        f.campaign_type,
        f.app_install,
        f.start_date,
        f.end_date,
        f.delivery_date,
        f.recurrent_deliverables,
        f.base_payment_per_creator_cents,
        f.commission,
    ]);

    const canLaunch = useMemo(() => {
        const objectiveLines = (f.objectives || []).filter((s: string) => String(s || '').trim());
        const hasObjectives =
            objectiveLines.length > 0 ||
            (Boolean(category) && Boolean(subCategory) && isValidSubForMain(category, subCategory));
        const productsOk = !giftOrBoth || (f.products || []).filter(Boolean).length > 0;
        const clippingOk = !f.is_clipping || isValidClippingAssetUrl(String(f.clipping_asset_url || ''));
        const premiumOk =
            !f.premium || (Array.isArray(f.premium_requirements) && (f.premium_requirements as string[]).length > 0);
        const syncOk = f.campaign_type !== 'sync_upload' || Boolean(String(f.sync_publish_date || '').trim());
        const isDirectHire = f.campaign_type === 'direct_hire';
        const isCommission = f.campaign_type === 'commission';
        const isAppInstall = Boolean((f.app_install as IAppInstall)?.enabled);
        const isPercentCommissionWithBasePay =
            isCommission && !isAppInstall && f.base_payment_per_creator_cents != null;
        const hasDeliverables =
            (isCommission && !isPercentCommissionWithBasePay) ||
            isAppInstall ||
            (f.recurrent_deliverables
                ? (f.deliverable_rounds || []).length > 0 &&
                  (f.deliverable_rounds || []).every((r: any) => r.delivery_date && (r.deliverables || []).length > 0)
                : (f.deliverables || []).filter((d: any) => d?.type && (isDirectHire || isCommission || d?.platform))
                      .length > 0);
        const commissionOk =
            !isCommission ||
            isAppInstall ||
            Boolean(sanitizeCommissionForPayload(f.commission as ICampaignCommissionConfig));
        const appInstallOk =
            !isAppInstall ||
            (((f.app_install as IAppInstall)?.kind === 'web_visit'
                ? Boolean(String((f.app_install as IAppInstall).web_fallback_url || '').trim())
                : Boolean(
                      String((f.app_install as IAppInstall).android_package || '').trim() ||
                          String((f.app_install as IAppInstall).android_store_url || '').trim() ||
                          String((f.app_install as IAppInstall).ios_store_url || '').trim(),
                  )) &&
                (!(f.app_install as IAppInstall)?.auto_reload_enabled ||
                    Boolean(String((f.app_install as IAppInstall).payment_method_id || '').trim())));
        const hasBrief = Boolean(plainTextFromHtml(String(f.description || '')));
        return (
            Boolean(activeBrand?._id) &&
            canAdvanceStep0 &&
            canAdvanceStep1 &&
            hasBrief &&
            hasObjectives &&
            productsOk &&
            clippingOk &&
            premiumOk &&
            syncOk &&
            hasDeliverables &&
            commissionOk &&
            appInstallOk
        );
    }, [
        activeBrand,
        canAdvanceStep0,
        canAdvanceStep1,
        f.objectives,
        f.products,
        f.deliverables,
        f.recurrent_deliverables,
        f.deliverable_rounds,
        f.description,
        f.is_clipping,
        f.clipping_asset_url,
        f.premium,
        f.premium_requirements,
        f.sync_publish_date,
        f.campaign_type,
        f.commission,
        f.app_install,
        f.base_payment_per_creator_cents,
        category,
        subCategory,
        giftOrBoth,
    ]);

    const handleAutoFill = useCallback(() => {
        if (step === 0) {
            const d = autoFillStep0();
            formDispatch({ type: 'FIELD', field: 'title', value: d.title });
            setCategory(d.category);
            setSubCategory(d.sub_category);
        } else if (step === 1) {
            const d = autoFillStep1();
            setBudgetInput(d.budget);
            setPublishDate(d.endDate);
            update('reward', d.reward);
            update('platforms', d.platforms);
            update('influencer_tier', d.tier);
            update('participants_count', d.participants);
            setParticipantsInput(String(d.participants));
            update('start_date', d.startDate);
            update('end_date', d.endDate);
        } else {
            const d = autoFillStep2();
            update('description', d.description);
            update('objectives', d.objectives);
            update('requirements', d.requirements);
            update('ideas', d.ideas);
            update('deliverables', d.deliverables);
            update('products', d.products);
            update('physical_product', d.physicalProduct);
            setExclusiveRights(d.exclusiveRights);
        }
    }, [step, update]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [step]);

    const prevStepRef = useRef(step);
    useEffect(() => {
        const prev = prevStepRef.current;
        prevStepRef.current = step;
        if (step === 1 && prev !== 1) {
            setParticipantsInput(String(f.participants_count ?? 1));
        }
    }, [step, f.participants_count]);

    useEffect(() => {
        if (step !== 2) return;
        const n = Math.max(1, parseInt(participantsInput.trim(), 10) || 1);
        if (n !== f.participants_count) update('participants_count', n);
    }, [step, participantsInput, f.participants_count, update]);

    useEffect(() => {
        if (step !== 2) return;
        if (f.campaign_type === 'commission') return;
        if (f.recurrent_deliverables) return; // rounds manage their own deliverables
        if ((f.deliverables || []).length > 0) return;
        const PLATFORM_DEFAULTS: Record<string, { type: string; platform: string; duration_range: string }> = {
            instagram: { type: 'instagram_reel', platform: 'instagram', duration_range: '15-30s' },
            tiktok: { type: 'tiktok_short', platform: 'tiktok', duration_range: '15-30s' },
            youtube: { type: 'youtube_short', platform: 'youtube', duration_range: '15-30s' },
        };
        const seeded = (f.platforms || [])
            .filter((p: string) => PLATFORM_DEFAULTS[p])
            .map((p: string) => ({ ...PLATFORM_DEFAULTS[p], count: 1 }));
        if (seeded.length > 0) update('deliverables', seeded);
    }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

    const { checkoutOpen, setCheckoutOpen, beginMonthly, beginLtd, onPaymentMethodReady, checkoutTitle } =
        useCampaignHostCheckout({
            onSubscriptionComplete: () => {
                setTierPickerOpen(false);
                setSubGuardOpen(false);
                reduxDispatch(fetchCampaignHostCreationAccess());
                reduxDispatch(fetchCampaignHostPricing());
                if (pendingLaunchAfterSubRef.current) {
                    pendingLaunchAfterSubRef.current = false;
                    handleLaunchRef.current();
                }
            },
        });

    const handleSelectMonthly = useCallback(
        (pkg: ICampaignHostPackage) => {
            setTierPickerOpen(false);
            beginMonthly(pkg);
        },
        [beginMonthly],
    );

    const handleSelectLtd = useCallback(
        (pkg: ICampaignHostPackage) => {
            setTierPickerOpen(false);
            beginLtd(pkg);
        },
        [beginLtd],
    );

    const openHostSubscriptionFlow = useCallback(() => {
        pendingLaunchAfterSubRef.current = false;
        reduxDispatch(fetchCampaignHostPricing());
        setTierPickerOpen(true);
    }, [reduxDispatch]);

    const guardedLaunch = useCallback(() => {
        if (!canLaunch || !activeBrand?._id) return;
        const isCommissionLaunch =
            f.campaign_type === 'commission' && !Boolean((f.app_install as IAppInstall)?.enabled);
        const access = creationAccess ?? hostPricing?.creation_access ?? null;
        const unlimitedCreate = Boolean(access?.unlimited || hasPlan);
        if (isCommissionLaunch && !unlimitedCreate) {
            reduxDispatch(fetchCampaignHostCreationAccess());
            reduxDispatch(fetchCampaignHostPricing());
            setSubGuardSubscriptionOnly(true);
            setSubGuardOpen(true);
            return;
        }
        if (!isCommissionLaunch && (!unlimitedCreate || creationAccessBlocksCreate(access))) {
            reduxDispatch(fetchCampaignHostCreationAccess());
            reduxDispatch(fetchCampaignHostPricing());
            setSubGuardSubscriptionOnly(false);
            setSubGuardOpen(true);
            return;
        }
        handleLaunchRef.current();
    }, [canLaunch, activeBrand, hasPlan, creationAccess, hostPricing, reduxDispatch, f.campaign_type, f.app_install]);

    const handleLaunch = (opts?: { bypassWalletShortfall?: boolean }) => {
        if (!canLaunch || !activeBrand?._id) return;
        const access = creationAccess ?? hostPricing?.creation_access ?? null;
        const isCommissionTab = f.campaign_type === 'commission';
        if (isCommissionTab && !(access?.unlimited || hasPlan)) {
            setSubGuardSubscriptionOnly(true);
            setSubGuardOpen(true);
            return;
        }
        // When saving a draft before wallet top-up, skip the shortfall gate (fee is on publish).
        if (!isCommissionTab && !opts?.bypassWalletShortfall && creationAccessBlocksCreate(access)) {
            setSubGuardSubscriptionOnly(false);
            setSubGuardOpen(true);
            return;
        }
        if (!canUseOauthCampaignModes && (f.campaign_type === 'sync_upload' || Boolean(f.premium))) {
            showToastMessage(
                `Premium and sync upload require Instagram, TikTok, and YouTube auto-link. Turn on auto-integration for ${
                    disabledAutoLinkLabel || 'all platforms'
                } in global config.`,
                'error',
            );
            return;
        }
        if (f.campaign_type === 'sync_upload' && f.recurrent_deliverables) {
            showToastMessage('Sync upload and a recurring series cannot both be on. Turn one off.', 'error');
            return;
        }
        sampleVideoFilesRef.current = sampleVideoFiles;
        const total_budget = parseBudgetInput(budgetInput);
        const objectiveLines = (f.objectives || []).filter((s: string) => String(s || '').trim());
        const objectivesPayload =
            objectiveLines.length > 0 ? objectiveLines : [`Target category: ${category} — ${subCategory}`];
        const req = (f.requirements || []).map((s: string) => String(s || '').trim()).filter(Boolean);
        const requirementsPayload = req;
        const isDirectHireSubmit = f.campaign_type === 'direct_hire';
        const isAppInstall = Boolean((f.app_install as IAppInstall)?.enabled);
        const isCommissionSubmit = isCommissionTab && !isAppInstall;
        const isPercentCommissionSubmit = isCommissionSubmit;
        const commissionPayload = isPercentCommissionSubmit
            ? sanitizeCommissionForPayload(f.commission as ICampaignCommissionConfig)
            : null;
        if (isPercentCommissionSubmit && !commissionPayload) {
            showToastMessage(
                f.commission?.target_type === 'external'
                    ? 'Add product name and a valid commission rate.'
                    : 'Select a contest and set a valid commission rate before launching.',
                'error',
            );
            return;
        }
        if (isAppInstall && total_budget <= 0) {
            showToastMessage('Enter a working budget for install / visit payouts.', 'error');
            return;
        }
        if (f.recurrent_deliverables && f.end_date) {
            const endDay = dayjs(f.end_date);
            const badRounds = (f.deliverable_rounds || []).filter(
                (r: any) => r.delivery_date && dayjs(r.delivery_date).isAfter(endDay),
            );
            if (badRounds.length > 0) {
                showToastMessage(
                    `${badRounds.length} ${
                        badRounds.length === 1 ? 'delivery has a date' : 'deliveries have dates'
                    } after the campaign end date. Please fix them before launching.`,
                    'error',
                );
                return;
            }
        }
        if (isAppInstall && (f.app_install as IAppInstall)?.auto_reload_enabled) {
            const pm = String((f.app_install as IAppInstall).payment_method_id || '').trim();
            if (!pm) {
                showToastMessage('Select a card on file to enable auto-reload, or turn auto-reload off.', 'error');
                return;
            }
        }
        const deliverablesPayload =
            isCommissionSubmit || isAppInstall
                ? (f.deliverables || []).filter((d: any) => d?.type)
                : (f.deliverables || [])
                      .filter((d: any) => d?.type && (isDirectHireSubmit || d?.platform))
                      .map((d: any) => {
                          if (isDirectHireSubmit) {
                              const { platform: _p, ...rest } = d;
                              return rest;
                          }
                          return d;
                      });
        const ideasPayload = (f.ideas || []).filter((s: string) => String(s || '').trim());
        const locationPayload = serializeCampaignLocation(f.location);
        const requestedCreators = Math.max(1, parseInt(participantsInput.trim(), 10) || f.participants_count || 1);
        const participants_count = clampCreatorsToPlanLimit(requestedCreators, maxCreators);
        if (invitedIds?.size && isOverCreatorPlanLimit(invitedIds.size, maxCreators)) {
            showToastMessage(
                creatorCapLabel || 'Too many invited creators for your plan. Upgrade to add more.',
                'error',
            );
            return;
        }
        const commissionBudget = commissionPayload ? commissionBudgetMajorUnits(commissionPayload) : null;
        const resolvedBudget = isAppInstall
            ? total_budget
            : commissionBudget != null && commissionBudget > 0
            ? commissionBudget
            : total_budget;
        createBaselineIdRef.current = lastCreatedCampaignId;
        reduxDispatch(
            createOffsiteCampaign({
                brand: activeBrand._id,
                title: form.title.trim(),
                description: String(form.description).trim(),
                total_budget: resolvedBudget,
                available_budget: resolvedBudget,
                participants_count,
                platforms: isCommissionSubmit
                    ? f.platforms?.length
                        ? f.platforms
                        : []
                    : isDirectHireSubmit
                    ? []
                    : f.platforms?.length
                    ? f.platforms
                    : ['instagram'],
                reward: isCommissionSubmit ? 'paid' : f.reward,
                influencer_tier: f.influencer_tier,
                objectives: objectivesPayload,
                requirements: requirementsPayload,
                ...(ideasPayload.length > 0 && { ideas: ideasPayload }),
                ...(f.recurrent_deliverables
                    ? {
                          recurrent_deliverables: true,
                          deliverable_rounds: (f.deliverable_rounds || []).map((r: any, i: number) => ({
                              ...r,
                              round_number: i + 1,
                          })),
                      }
                    : {
                          ...((isCommissionSubmit || isAppInstall) && deliverablesPayload.length === 0
                              ? {}
                              : { deliverables: deliverablesPayload }),
                      }),
                products: isCommissionSubmit ? [] : (f.products || []).filter(Boolean),
                physical_product: isCommissionSubmit ? false : Boolean(f.physical_product),
                exclusive_rights: isCommissionSubmit ? false : Boolean(exclusiveRights),
                campaign_type: isCommissionSubmit
                    ? 'commission'
                    : f.campaign_type === 'direct_hire'
                    ? 'direct_hire'
                    : f.campaign_type === 'sync_upload'
                    ? 'sync_upload'
                    : 'standard',
                ...(f.campaign_type === 'sync_upload'
                    ? { sync_publish_date: String(f.sync_publish_date || '').trim() }
                    : {}),
                ...(isPercentCommissionSubmit &&
                    commissionPayload && {
                        commission: commissionPayload,
                        influencer_readme: String(f.influencer_readme || '').trim() || undefined,
                    }),
                is_clipping: isCommissionSubmit ? false : Boolean(f.is_clipping),
                ...(!isCommissionSubmit &&
                    f.is_clipping &&
                    isValidClippingAssetUrl(String(f.clipping_asset_url || '')) && {
                        clipping_asset_url: String(f.clipping_asset_url).trim(),
                    }),
                premium: isCommissionSubmit ? false : Boolean(f.premium),
                premium_requirements: isCommissionSubmit
                    ? []
                    : f.premium
                    ? ((f.premium_requirements || []) as PremiumRequirement[])
                    : [],
                ...(locationPayload && { location: locationPayload }),
                world_wide: Boolean(f.world_wide),
                ...(isAppInstall && {
                    app_install: f.app_install as IAppInstall,
                }),
                ...(f.start_date && { start_date: f.start_date }),
                ...((f.end_date || publishDate) && { end_date: f.end_date || publishDate }),
                publish_date: f.end_date || publishDate,
                ...(!f.recurrent_deliverables && f.delivery_date && { delivery_date: f.delivery_date }),
                ...((isCommissionSubmit || isAppInstall) &&
                    f.base_payment_per_creator_cents != null && {
                        base_payment_per_creator_cents: f.base_payment_per_creator_cents,
                    }),
                ...(featureImageFile && { rawFeatureImage: featureImageFile }),
                ...(appIconFile && { rawAppIconFile: appIconFile }),
                ...(assets.length > 0 && {
                    rawAssets: assets.map(a => ({
                        url: a.url,
                        file: a.file,
                        description: a.description || undefined,
                        requirements: a.requirements.length > 0 ? a.requirements : undefined,
                    })),
                }),
                ...(invitedIds && invitedIds.size > 0 && { invited_influencers: Array.from(invitedIds) }),
                category,
                sub_category: subCategory,
            }),
        );
        submittingRef.current = true;
    };

    // Keep ref always pointing to the latest handleLaunch closure
    handleLaunchRef.current = handleLaunch;

    const labelSx = {
        fontSize: 11,
        fontFamily: FONT_ENUM.MAIN_BOLD,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.14em',
        color: t.primary_white,
        mb: 1.5,
    };

    const budgetFormatted = parseBudgetInput(budgetInput);
    const commissionSeats = Math.max(1, parseInt(participantsInput.trim(), 10) || f.participants_count || 1);
    const commissionFooterSummary = isPercentCommission
        ? commissionWizardFooterSummary(f.commission as ICampaignCommissionConfig, commissionSeats)
        : null;
    const commissionBasePayTotalCents =
        isPercentCommission && f.base_payment_per_creator_cents
            ? Math.round(Number(f.base_payment_per_creator_cents)) * commissionSeats
            : 0;
    const appInstallSeats = Math.max(1, parseInt(participantsInput.trim(), 10) || f.participants_count || 1);
    const appInstallBasePayTotal =
        isAppInstall && f.base_payment_per_creator_cents
            ? (Math.round(Number(f.base_payment_per_creator_cents)) / 100) * appInstallSeats
            : 0;
    const summaryBudget = isPercentCommission
        ? (() => {
              const poolCents = resolveCommissionDisplayPoolCents(f.commission as ICampaignCommissionConfig);
              const combinedCents = poolCents + commissionBasePayTotalCents;
              return combinedCents > 0
                  ? formatCommissionPoolDollars(combinedCents)
                  : commissionFooterSummary?.totalValue;
          })()
        : budgetFormatted > 0
        ? (() => {
              const total = budgetFormatted + appInstallBasePayTotal;
              return `$${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          })()
        : undefined;

    const footerFeeBreakdown = step >= 1 && paidOrBoth ? paidCampaignFeeBreakdown : null;

    const tierSummaryLabel = useMemo(() => {
        const tm = activeTiers.find(t => t.id === f.influencer_tier);
        return tm ? `${tm.name} (${tm.reach})` : paidCampaignFeeBreakdown?.tier.name ?? '';
    }, [f.influencer_tier, activeTiers, paidCampaignFeeBreakdown?.tier.name]);

    const hostSubscribeUpsell = useMemo(() => {
        if (hasPlan || fetchingHostPlan || !paidOrBoth || !paidCampaignFeeBreakdown) return null;

        const subscribedMin = paidCampaignFeeBreakdown.minPayoutPerCreatorSubscribed;
        const unsubscribedMin =
            paidCampaignFeeBreakdown.minPayoutPerCreatorUnsubscribed ?? paidCampaignFeeBreakdown.minimumPerParticipant;
        const budget = parseBudgetInput(budgetInput);
        if (budget <= 0 || subscribedMin == null) return null;

        const extraCreators = computeExtraCreatorsAtSubscribedRate({
            totalBudgetUsd: budget,
            unsubscribedMinPerCreatorUsd: unsubscribedMin,
            subscribedMinPerCreatorUsd: subscribedMin,
            platformFeesUsd: paidCampaignFeeBreakdown.totalPlatformFee,
        });

        if (extraCreators == null) return null;

        return {
            extraCreators,
            currentRateFormatted: formatCurrency(unsubscribedMin),
            subscribedRateFormatted: formatCurrency(subscribedMin),
        };
    }, [hasPlan, fetchingHostPlan, paidOrBoth, paidCampaignFeeBreakdown, budgetInput]);

    return (
        <>
            <BrandCampaignStepper
                currentStep={step}
                totalSteps={TOTAL_STEPS}
                stepLabel={step === 1 && isCommission ? 'Seats & Dates' : STEP_LABELS[step]}
                stepSubtitle={draftHint && step === 0 ? draftHint : undefined}
                onAutoFill={handleAutoFill}
            />

            {step === 0 && (
                <StepIdentity
                    form={form}
                    formDispatch={formDispatch}
                    category={category}
                    subCategory={subCategory}
                    onSelectCategory={selectCategory}
                    onSubCategoryChange={setSubCategory}
                    t={t}
                    fieldSx={fieldSx}
                    labelSx={labelSx}
                    hasHostPlan={hasPlan}
                    onRequireHostSubscription={() => {
                        reduxDispatch(fetchCampaignHostPricing());
                        setSubGuardSubscriptionOnly(true);
                        setSubGuardOpen(true);
                    }}
                    featureImagePreview={featureImagePreview}
                    onFeatureImageSelect={(file, preview) => {
                        setFeatureImageFile(file);
                        setFeatureImagePreview(preview);
                    }}
                    onFeatureImageClear={() => {
                        setFeatureImageFile(null);
                        setFeatureImagePreview(null);
                    }}
                    onAppIconFile={setAppIconFile}
                    activeTiers={activeTiers}
                    canUseOauthCampaignModes={canUseOauthCampaignModes}
                    disabledAutoLinkLabel={disabledAutoLinkLabel}
                    campaignPlatforms={campaignPlatforms}
                />
            )}
            {step === 1 && (
                <StepBudgetTargeting
                    form={form}
                    update={update}
                    budgetInput={budgetInput}
                    setBudgetInput={setBudgetInput}
                    publishDate={publishDate}
                    setPublishDate={setPublishDate}
                    participantsInput={participantsInput}
                    setParticipantsInput={setParticipantsInput}
                    activeTiers={activeTiers}
                    campaignPlatforms={campaignPlatforms}
                    t={t}
                    fieldSx={fieldSx}
                    budgetFieldSx={budgetFieldSx}
                    labelSx={labelSx}
                    maxCreators={maxCreators}
                    creatorCapLabel={creatorCapLabel}
                    billingCopy={billingCopy}
                    hostSubscribeUpsell={hostSubscribeUpsell}
                    onOpenHostSubscription={openHostSubscriptionFlow}
                    hasPlan={hasPlan}
                    feeBreakdown={paidCampaignFeeBreakdown ?? commissionTierBreakdown}
                />
            )}
            {step === 2 && (
                <StepDetailsLaunch
                    form={form}
                    update={update}
                    category={category}
                    subCategory={subCategory}
                    exclusiveRights={exclusiveRights}
                    setExclusiveRights={setExclusiveRights}
                    giftOrBoth={giftOrBoth}
                    t={t}
                    fieldSx={fieldSx}
                    labelSx={labelSx}
                    assets={assets}
                    onAssetsChange={setAssets}
                    sampleVideoFiles={sampleVideoFiles}
                    onSampleVideoFilesChange={setSampleVideoFiles}
                />
            )}

            <BrandCampaignStepFooter
                onBack={step > 0 ? () => setStep(s => s - 1) : undefined}
                onNext={
                    step < TOTAL_STEPS - 1
                        ? () => {
                              const nextStep = step + 1;
                              persistDraft(nextStep);
                              setStep(nextStep);
                          }
                        : guardedLaunch
                }
                onSaveDraft={handleSaveDraft}
                draftSavedHint={draftHint && !draftHint.startsWith('Draft restored') ? draftHint : null}
                nextLabel={
                    step < TOTAL_STEPS - 1 ? `Continue to Step ${step + 2}` : creating ? 'Creating…' : 'Launch Campaign'
                }
                disableNext={
                    (step === 0 && !canAdvanceStep0) ||
                    (step === 1 && !canAdvanceStep1) ||
                    (step === 2 && (!canLaunch || creating))
                }
                summaryBudgetLabel={
                    step >= 1 && isPercentCommission
                        ? commissionBasePayTotalCents > 0
                            ? 'Possible total'
                            : commissionFooterSummary?.totalLabel
                        : undefined
                }
                summaryBudgetValue={step >= 1 ? summaryBudget : undefined}
                summaryStats={
                    step >= 1 && !isCommission && enabledSelectedPlatforms.length > 0
                        ? [
                              {
                                  label: `${enabledSelectedPlatforms.length} Platform${
                                      enabledSelectedPlatforms.length > 1 ? 's' : ''
                                  }`,
                              },
                          ]
                        : step >= 1 && isPercentCommission && commissionFooterSummary
                        ? [
                              { label: commissionFooterSummary.formulaLabel },
                              ...(commissionBasePayTotalCents > 0
                                  ? [{ label: `+${formatCommissionPoolDollars(commissionBasePayTotalCents)} base pay` }]
                                  : []),
                          ]
                        : step >= 1 && isAppInstall
                        ? [{ label: 'App / web CPA budget' }]
                        : undefined
                }
                summaryBudgetDetail={
                    step >= 1 && paidOrBoth && footerFeeBreakdown ? (
                        <Box sx={{ mt: summaryBudget ? 1.5 : 0.5 }}>
                            <Typography
                                sx={{
                                    fontSize: 9,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.16em',
                                    color: BRAND_LUXE_PRIMARY,
                                    mb: 1,
                                    textAlign: 'right',
                                }}>
                                Budget breakdown
                            </Typography>
                            <Typography
                                component="div"
                                sx={{
                                    fontSize: 11,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: 'rgba(255,255,255,0.72)',
                                    lineHeight: 1.5,
                                    textAlign: 'right',
                                }}>
                                Min. creator pool ({tierSummaryLabel}):{' '}
                                {formatCurrency(footerFeeBreakdown.minimumPerParticipant)} ×{' '}
                                {footerFeeBreakdown.participantsUsed}
                                {footerFeeBreakdown.deliveryRoundsUsed > 1
                                    ? ` × ${footerFeeBreakdown.deliveryRoundsUsed} deliveries`
                                    : ''}{' '}
                                = {formatCurrency(footerFeeBreakdown.minimumParticipantPay)}
                                <br />
                                {footerFeeBreakdown.totalPlatformFee > 0 ? (
                                    <>
                                        Platform fees: {formatCurrency(footerFeeBreakdown.platformFeePerPlatform)} ×{' '}
                                        {enabledSelectedPlatforms.length}
                                        {footerFeeBreakdown.deliveryRoundsUsed > 1 ? ' (once)' : ''} ={' '}
                                        {formatCurrency(footerFeeBreakdown.totalPlatformFee)}
                                        <br />
                                    </>
                                ) : null}
                                {!hasPlan &&
                                footerFeeBreakdown.minPayoutPerCreatorSubscribed != null &&
                                footerFeeBreakdown.minimumParticipantPayIfSubscribed != null &&
                                footerFeeBreakdown.hostMinPayoutSubscribeToSavePerCreatorUsd != null &&
                                footerFeeBreakdown.hostMinPayoutSubscribeToSavePerCreatorUsd > 0 ? (
                                    <>
                                        With host subscription:{' '}
                                        {formatCurrency(footerFeeBreakdown.minPayoutPerCreatorSubscribed)} ×{' '}
                                        {footerFeeBreakdown.participantsUsed}
                                        {footerFeeBreakdown.deliveryRoundsUsed > 1
                                            ? ` × ${footerFeeBreakdown.deliveryRoundsUsed} deliveries`
                                            : ''}{' '}
                                        = {formatCurrency(footerFeeBreakdown.minimumParticipantPayIfSubscribed)}
                                        <br />
                                        <Box component="span" sx={{ color: BRAND_LUXE_PRIMARY }}>
                                            Subscribe to hire creators from{' '}
                                            {formatCurrency(footerFeeBreakdown.minPayoutPerCreatorSubscribed)}
                                            /creator
                                            {footerFeeBreakdown.deliveryRoundsUsed > 1 ? ' per delivery' : ''} instead
                                            of{' '}
                                            {formatCurrency(
                                                footerFeeBreakdown.minPayoutPerCreatorUnsubscribed ??
                                                    footerFeeBreakdown.minimumPerParticipant,
                                            )}
                                        </Box>
                                        <br />
                                    </>
                                ) : null}
                                Creators + platforms (minimum):{' '}
                                {formatCurrency(footerFeeBreakdown.calculatedMinimumTotal)}
                                {footerFeeBreakdown.policyFloorApplied ? (
                                    <>
                                        <br />
                                        <Box component="span" sx={{ color: BRAND_LUXE_PRIMARY }}>
                                            {formatCurrency(ugcBudget.absolute_min_total_budget_usd)} campaign minimum
                                            applied
                                        </Box>
                                    </>
                                ) : null}
                                <br />
                                <Box component="span" sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff' }}>
                                    Minimum to fund: {formatCurrency(footerFeeBreakdown.minimumRequiredBudget)}
                                </Box>
                                {budgetFormatted > 0 ? (
                                    <>
                                        <br />
                                        Allocated per creator
                                        {footerFeeBreakdown.deliveryRoundsUsed > 1 ? ' / delivery' : ''} (your total):{' '}
                                        {formatCurrency(footerFeeBreakdown.actualPerParticipant)}
                                    </>
                                ) : null}
                                {footerFeeBreakdown.deliveryRoundsUsed > 1 ? (
                                    <>
                                        <br />
                                        <Box component="span" sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff' }}>
                                            {budgetFormatted + 0.01 < footerFeeBreakdown.minimumRequiredBudget
                                                ? 'Minimum earning per creator'
                                                : 'Total earning per creator'}
                                            : {formatCurrency(footerFeeBreakdown.actualPerParticipant)} ×{' '}
                                            {footerFeeBreakdown.deliveryRoundsUsed} deliveries ={' '}
                                            {formatCurrency(
                                                footerFeeBreakdown.actualPerParticipant *
                                                    footerFeeBreakdown.deliveryRoundsUsed,
                                            )}
                                        </Box>
                                    </>
                                ) : null}
                                {budgetFormatted > 0 &&
                                budgetFormatted + 0.01 < footerFeeBreakdown.minimumRequiredBudget ? (
                                    <>
                                        <br />
                                        <Box
                                            component="span"
                                            sx={{ color: '#f87171', fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                            Below minimum — add funds to continue.
                                        </Box>
                                    </>
                                ) : null}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: 'rgba(255,255,255,0.55)',
                                    mt: 1.25,
                                    lineHeight: 1.45,
                                    textAlign: 'right',
                                    fontStyle: 'italic',
                                }}>
                                The higher your budget beyond this floor, the more likely creators are to apply and
                                prioritize your campaign.
                            </Typography>
                        </Box>
                    ) : undefined
                }
            />

            <BrandCampaignSubscriptionGuard
                open={subGuardOpen}
                flatFeeCents={perCampaignFeeCents}
                shortfallCents={walletShortfallCents}
                subscriptionOnly={subGuardSubscriptionOnly || f.campaign_type === 'commission'}
                onClose={() => {
                    setSubGuardOpen(false);
                    setSubGuardSubscriptionOnly(false);
                }}
                onSubscribe={() => {
                    pendingLaunchAfterSubRef.current = true;
                    setSubGuardOpen(false);
                    setSubGuardSubscriptionOnly(false);
                    setTierPickerOpen(true);
                }}
                onContinueWithFee={() => {
                    setSubGuardOpen(false);
                    setSubGuardSubscriptionOnly(false);
                    afterCreateNavigateRef.current = 'detail';
                    handleLaunchRef.current();
                }}
                onFundWallet={() => {
                    setSubGuardOpen(false);
                    setSubGuardSubscriptionOnly(false);
                    afterCreateNavigateRef.current = 'wallet';
                    handleLaunchRef.current({ bypassWalletShortfall: true });
                }}
            />

            <CampaignHostTierPickerModal
                open={tierPickerOpen}
                onClose={() => {
                    setTierPickerOpen(false);
                    pendingLaunchAfterSubRef.current = false;
                }}
                onSelectMonthly={handleSelectMonthly}
                onSelectLtd={handleSelectLtd}
            />

            <CampaignHostCheckoutDialog
                open={checkoutOpen}
                title={checkoutTitle}
                onClose={() => setCheckoutOpen(false)}
                onPaymentMethodReady={onPaymentMethodReady}
            />
        </>
    );
}

/* ──────────── Step 0: Campaign Identity ──────────── */

function StepIdentity({
    form,
    formDispatch,
    category,
    subCategory,
    onSelectCategory,
    onSubCategoryChange,
    t,
    fieldSx,
    labelSx,
    featureImagePreview,
    onFeatureImageSelect,
    onFeatureImageClear,
    onAppIconFile,
    hasHostPlan,
    onRequireHostSubscription,
    activeTiers,
    canUseOauthCampaignModes,
    disabledAutoLinkLabel,
    campaignPlatforms,
}: {
    form: any;
    formDispatch: React.Dispatch<any>;
    category: string;
    subCategory: string;
    onSelectCategory: (main: string) => void;
    onSubCategoryChange: (sub: string) => void;
    t: Colors;
    fieldSx: any;
    labelSx: any;
    featureImagePreview: string | null;
    onFeatureImageSelect: (file: File, preview: string) => void;
    onFeatureImageClear: () => void;
    onAppIconFile?: (file: File | null) => void;
    hasHostPlan?: boolean;
    onRequireHostSubscription?: () => void;
    activeTiers: { id: string; name: string; reach: string }[];
    canUseOauthCampaignModes: boolean;
    disabledAutoLinkLabel: string;
    campaignPlatforms: { id: string; label: string; enabled: boolean; coming_soon?: boolean }[];
}) {
    const { catalog, popular } = useContentCategories();
    const nicheOptionsAlphabetical = useMemo(
        () =>
            buildNicheOptionsAlphabetical(
                catalog.mainsAlphabetical.length
                    ? catalog.mainsAlphabetical
                    : (NICHE_MAIN_ALPHABETICAL as unknown as string[]),
            ),
        [catalog.mainsAlphabetical],
    );
    const popularOptions = useMemo(() => {
        const names = popular.length
            ? popular
            : nicheOptionsAlphabetical.slice(0, MAIN_CATEGORY_QUICK_PICK).map(o => o.key);
        return names.map(key => ({
            key,
            label: key,
            Icon: iconForNicheMain(key),
        }));
    }, [popular, nicheOptionsAlphabetical]);
    const subCategoryOptions = useMemo(
        () =>
            catalog.source === 'api'
                ? getSubcategoriesForMainFromCatalog(catalog, category)
                : getSubcategoriesForMain(category),
        [catalog, category],
    );

    const { isLoaded: isMapsLoaded } = useGoogleMapsLoader();
    const locationSuggestionPalette: PlacesSuggestionPalette = {
        rowInactiveBg: BRAND_SURFACE_CONTAINER_HIGHEST,
        rowInactiveColor: 'rgba(255,255,255,0.9)',
        rowActiveBg: BRAND_LUXE_PRIMARY,
        rowActiveColor: '#392700',
        loadingBg: BRAND_SURFACE_CONTAINER_HIGHEST,
        loadingColor: 'rgba(255,255,255,0.9)',
        listBorder: `1px solid ${BRAND_OUTLINE_VARIANT}`,
    };
    const placesFieldSx = {
        '& .MuiOutlinedInput-root': {
            bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
            borderRadius: 2,
            color: t.primary_white,
            '& fieldset': { border: 'none' },
            '&:hover fieldset': { border: 'none' },
            '&.Mui-focused fieldset': { border: 'none' },
            '&.Mui-focused': { boxShadow: '0 0 0 1px rgba(236,170,0,0.35)' },
        },
        '& .MuiInputLabel-root': {
            color: 'rgba(255,255,255,0.65)',
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
        },
        '& .MuiOutlinedInput-input': { color: t.primary_white },
        '& .MuiOutlinedInput-input::placeholder': { color: t.form_label, opacity: 1 },
    };
    const locationValue: ILocation = form.location ?? {
        type: 'Point',
        coordinates: [],
        address: '',
        description: '',
        short_name: '',
    };

    // const isDirectHire = form.campaign_type === 'direct_hire';

    const HIRE_TABS = [
        {
            value: 'standard',
            label: 'Non-direct hire',
            description:
                'Creators publish your content and submit a live social media link for verification. You can optionally require all creators to publish simultaneously.',
        },
        {
            value: 'direct_hire',
            label: 'Direct hire',
            description:
                'Creators submit completed content directly to you, and you decide when and where to publish it',
        },
        {
            value: 'commission',
            label: 'Commission',
            description:
                'Subscribers only. Pay for performance — contest/product % commission, or app installs / web visits (CPA). Creators earn after attributed results.',
        },
    ] as const;

    const activeCampaignTab =
        form.campaign_type === 'direct_hire'
            ? 'direct_hire'
            : form.campaign_type === 'commission'
            ? 'commission'
            : 'standard';
    const isCommission = form.campaign_type === 'commission';
    const isAppInstall = Boolean((form as any).app_install?.enabled);
    const isPercentCommission = isCommission && !isAppInstall;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            {/* Campaign type tabs */}
            <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                <Typography sx={labelSx}>Campaign type</Typography>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                        borderRadius: 2,
                        p: 0.75,
                        flexWrap: 'wrap',
                    }}>
                    {HIRE_TABS.map(tab => (
                        <Button
                            key={tab.value}
                            fullWidth
                            onClick={() => {
                                if (tab.value === 'commission' && !hasHostPlan) {
                                    onRequireHostSubscription?.();
                                    return;
                                }
                                formDispatch({ type: 'FIELD', field: 'campaign_type', value: tab.value });
                                if (tab.value === 'commission') {
                                    if (!(form as any).commission) {
                                        formDispatch({
                                            type: 'FIELD',
                                            field: 'commission',
                                            value: emptyCommissionConfig('contest'),
                                        });
                                    }
                                    formDispatch({ type: 'FIELD', field: 'reward', value: 'paid' });
                                    formDispatch({ type: 'FIELD', field: 'is_clipping', value: false });
                                    formDispatch({ type: 'FIELD', field: 'clipping_asset_url', value: '' });
                                    formDispatch({ type: 'FIELD', field: 'premium', value: false });
                                    formDispatch({ type: 'FIELD', field: 'premium_requirements', value: [] });
                                    formDispatch({ type: 'FIELD', field: 'physical_product', value: false });
                                    formDispatch({ type: 'FIELD', field: 'products', value: [] });
                                } else {
                                    // App/web CPA lives under Commission only
                                    formDispatch({
                                        type: 'FIELD',
                                        field: 'app_install',
                                        value: { enabled: false },
                                    });
                                }
                            }}
                            sx={{
                                flex: { xs: '1 1 100%', sm: '1 1 30%' },
                                borderRadius: 1.5,
                                py: 1.25,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 13,
                                textTransform: 'none',
                                letterSpacing: 0,
                                bgcolor: activeCampaignTab === tab.value ? BRAND_LUXE_PRIMARY : 'transparent',
                                color: activeCampaignTab === tab.value ? '#392700' : 'rgba(255,255,255,0.55)',
                                '&:hover': {
                                    bgcolor:
                                        activeCampaignTab === tab.value ? BRAND_LUXE_PRIMARY : 'rgba(255,255,255,0.06)',
                                },
                                transition: 'background-color 0.2s, color 0.2s',
                            }}>
                            {tab.label}
                        </Button>
                    ))}
                </Box>
                <Typography
                    sx={{
                        mt: 1.5,
                        fontSize: 13,
                        color: 'rgba(255,255,255,0.5)',
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        lineHeight: 1.5,
                    }}>
                    {HIRE_TABS.find(t => t.value === activeCampaignTab)?.description}
                </Typography>
            </Box>

            {isCommission ? (
                <AppInstallCampaignToggle
                    value={((form as any).app_install as IAppInstall) ?? { enabled: false }}
                    onChange={next => formDispatch({ type: 'FIELD', field: 'app_install', value: next })}
                    onAppIconFile={onAppIconFile}
                    influencerTier={form.influencer_tier}
                    fieldSx={fieldSx}
                    labelSx={labelSx}
                />
            ) : null}

            {isPercentCommission ? (
                <CommissionCampaignFields
                    value={((form as any).commission as ICampaignCommissionConfig) || emptyCommissionConfig('contest')}
                    influencerReadme={String((form as any).influencer_readme || '')}
                    onChange={next => formDispatch({ type: 'FIELD', field: 'commission', value: next })}
                    onReadmeChange={readme =>
                        formDispatch({ type: 'FIELD', field: 'influencer_readme', value: readme })
                    }
                    brandSurface={BRAND_SURFACE_CONTAINER_LOW}
                />
            ) : null}

            {isCommission ? (
                <UgcTargetingConfigPanel>
                    {isPercentCommission && (
                        <UgcTargetingSection>
                            <UgcTargetingSectionLabel>Platforms</UgcTargetingSectionLabel>
                            <UgcPlatformChips
                                platforms={campaignPlatforms}
                                selectedIds={form.platforms || []}
                                onToggle={platformId => {
                                    const cur = form.platforms || [];
                                    const selected = cur.includes(platformId);
                                    formDispatch({
                                        type: 'FIELD',
                                        field: 'platforms',
                                        value: selected
                                            ? cur.filter((p: string) => p !== platformId)
                                            : [...cur, platformId],
                                    });
                                }}
                            />
                        </UgcTargetingSection>
                    )}
                    <UgcTargetingSection>
                        <UgcTargetingSectionLabel>Influencer tier</UgcTargetingSectionLabel>
                        <Typography
                            sx={{
                                fontSize: 13,
                                color: 'rgba(255,255,255,0.55)',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                mb: 1.5,
                                lineHeight: 1.5,
                            }}>
                            Creators must meet this tier to apply to this commission campaign.
                        </Typography>
                        <UgcInfluencerTierCards
                            tiers={activeTiers}
                            selectedTierId={form.influencer_tier}
                            onSelect={tierId =>
                                formDispatch({ type: 'FIELD', field: 'influencer_tier', value: tierId })
                            }
                        />
                    </UgcTargetingSection>
                </UgcTargetingConfigPanel>
            ) : null}

            {form.campaign_type !== 'direct_hire' &&
                form.campaign_type !== 'commission' &&
                canUseOauthCampaignModes && (
                    <Box
                        sx={{
                            bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                            p: { xs: 2, sm: 3 },
                            borderRadius: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}>
                        <Box
                            sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 17, color: t.primary_white }}>
                                    Sync upload
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 14,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: 'rgba(255,255,255,0.72)',
                                        mt: 0.75,
                                        lineHeight: 1.55,
                                    }}>
                                    Creators all publish on the same day — ideal for flooding the zone and going viral
                                    in one coordinated push. Creators must auto-link their accounts with OAuth, and
                                    content must be created and brand-approved at least 3 days before the publish date.
                                </Typography>
                                {form.recurrent_deliverables && form.campaign_type !== 'sync_upload' ? (
                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: BRAND_LUXE_PRIMARY,
                                            mt: 1,
                                            lineHeight: 1.5,
                                        }}>
                                        Recurring series is on, so sync upload stays off. Turn off recurring
                                        deliverables first — they cannot both be on.
                                    </Typography>
                                ) : null}
                                {form.recurrent_deliverables && form.campaign_type === 'sync_upload' ? (
                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: BRAND_LUXE_PRIMARY,
                                            mt: 1,
                                            lineHeight: 1.5,
                                        }}>
                                        Sync upload and a recurring series cannot both be on. Turn one off before you
                                        launch.
                                    </Typography>
                                ) : null}
                            </Box>
                            <Switch
                                size="small"
                                checked={form.campaign_type === 'sync_upload'}
                                disabled={Boolean(form.recurrent_deliverables) && form.campaign_type !== 'sync_upload'}
                                onChange={(_, v) =>
                                    formDispatch({
                                        type: 'FIELD',
                                        field: 'campaign_type',
                                        value: v ? 'sync_upload' : 'standard',
                                    })
                                }
                                inputProps={{ 'aria-label': 'Sync upload' }}
                                sx={{
                                    flexShrink: 0,
                                    mt: 0.25,
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: BRAND_LUXE_SECONDARY,
                                        '& + .MuiSwitch-track': { backgroundColor: SECONDARY_CONTAINER, opacity: 1 },
                                    },
                                    '& .MuiSwitch-track': { opacity: 1, backgroundColor: '#484847' },
                                }}
                            />
                        </Box>
                        {form.campaign_type === 'sync_upload' && (
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 1.5,
                                    bgcolor: 'rgba(255,201,101,0.08)',
                                    border: '1px solid rgba(255,201,101,0.28)',
                                }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 13,
                                        color: BRAND_LUXE_PRIMARY,
                                        mb: 0.75,
                                    }}>
                                    Sync upload requirements
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: 'rgba(255,255,255,0.82)',
                                        lineHeight: 1.55,
                                    }}>
                                    Applicants will be told that once accepted, content must be ready and approved 3
                                    days before the publish date, and that social accounts must be OAuth auto-linked
                                    (not manual usernames).
                                </Typography>
                            </Box>
                        )}
                        {form.campaign_type === 'sync_upload' && (
                            <Box>
                                <Typography sx={{ ...labelSx, mb: 1 }}>
                                    Publish date{' '}
                                    <Typography
                                        component="span"
                                        sx={{ color: '#f87171', fontFamily: 'inherit', fontSize: 'inherit' }}>
                                        *
                                    </Typography>
                                </Typography>
                                <CustomDatePicker
                                    value={form.sync_publish_date || undefined}
                                    onChange={val =>
                                        formDispatch({ type: 'FIELD', field: 'sync_publish_date', value: val || '' })
                                    }
                                    minDate={new Date(Date.now() + 86400000).toISOString()}
                                    withTime={false}
                                    withTimezone={false}
                                    sx={ugcBudgetDatePickerSx}
                                />
                                {!String(form.sync_publish_date || '').trim() ? (
                                    <Typography
                                        sx={{
                                            mt: 1,
                                            fontSize: 12,
                                            color: '#f87171',
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        }}>
                                        Required for sync upload campaigns.
                                    </Typography>
                                ) : null}{' '}
                            </Box>
                        )}
                    </Box>
                )}

            {form.campaign_type !== 'direct_hire' &&
            form.campaign_type !== 'commission' &&
            !canUseOauthCampaignModes ? (
                <Box
                    sx={{
                        bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                        p: { xs: 2, sm: 3 },
                        borderRadius: 2,
                        border: '1px solid rgba(255,201,101,0.22)',
                    }}>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 15, color: '#fff', mb: 0.75 }}>
                        Premium & sync upload unavailable
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 13,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: 'rgba(255,255,255,0.72)',
                            lineHeight: 1.55,
                        }}>
                        Instagram, TikTok, and YouTube auto-integration must all be on in global config. Currently off:{' '}
                        {disabledAutoLinkLabel || 'one or more platforms'}.
                    </Typography>
                </Box>
            ) : null}

            {!isCommission ? (
                <ClippingCampaignToggle
                    checked={Boolean(form.is_clipping)}
                    assetUrl={String(form.clipping_asset_url || '')}
                    onCheckedChange={v => {
                        formDispatch({ type: 'FIELD', field: 'is_clipping', value: v });
                        if (!v) formDispatch({ type: 'FIELD', field: 'clipping_asset_url', value: '' });
                    }}
                    onAssetUrlChange={url => formDispatch({ type: 'FIELD', field: 'clipping_asset_url', value: url })}
                    fieldSx={fieldSx}
                    labelSx={labelSx}
                />
            ) : null}

            {!isCommission && canUseOauthCampaignModes ? (
                <PremiumCampaignToggle
                    premium={Boolean(form.premium)}
                    requirements={(form.premium_requirements || []) as PremiumRequirement[]}
                    onPremiumChange={v => formDispatch({ type: 'FIELD', field: 'premium', value: v })}
                    onRequirementsChange={reqs =>
                        formDispatch({ type: 'FIELD', field: 'premium_requirements', value: reqs })
                    }
                />
            ) : null}

            {/* Feature Image — top of step 1 */}
            <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                <Typography sx={labelSx}>
                    Feature image{' '}
                    <Typography component="span" sx={{ color: '#f87171', fontFamily: 'inherit', fontSize: 'inherit' }}>
                        *
                    </Typography>
                    <RichMediaShareSizeHint inline sx={{ color: 'rgba(255,255,255,0.45)' }} />
                </Typography>
                <Typography
                    sx={{
                        fontSize: 13,
                        color: 'rgba(255,255,255,0.55)',
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        lineHeight: 1.5,
                        mb: 1.5,
                    }}>
                    Place a nice image here that best describes your campaign.
                </Typography>
                <CampaignFeatureImagePicker
                    preview={featureImagePreview}
                    onSelect={onFeatureImageSelect}
                    onClear={onFeatureImageClear}
                />
            </Box>

            <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                <Typography sx={labelSx}>Campaign title</Typography>
                <TextField
                    fullWidth
                    placeholder="Midnight Summer Drop 2024"
                    value={form.title}
                    onChange={e => formDispatch({ type: 'FIELD', field: 'title', value: e.target.value })}
                    sx={{
                        ...fieldSx,
                        '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], py: 0.5 },
                        '& .MuiInputBase-input': {
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 18,
                            color: t.primary_white,
                        },
                    }}
                />
            </Box>
            <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                {isMapsLoaded ? (
                    <PlacesAutocompleteField
                        label="Location"
                        placeholder="London, UK — start typing, then pick a suggestion"
                        value={locationValue}
                        onChange={loc => {
                            const single = Array.isArray(loc) ? loc[0] : loc;
                            formDispatch({ type: 'FIELD', field: 'location', value: single });
                        }}
                        textFieldSx={placesFieldSx}
                        suggestionPalette={locationSuggestionPalette}
                    />
                ) : (
                    <>
                        <Typography sx={labelSx}>Location</Typography>
                        <TextField
                            fullWidth
                            placeholder="London, UK"
                            value={locationValue.address || ''}
                            onChange={e =>
                                formDispatch({
                                    type: 'FIELD',
                                    field: 'location',
                                    value: {
                                        type: 'Point',
                                        coordinates: [],
                                        address: e.target.value,
                                        description: e.target.value,
                                        short_name: e.target.value.split(',')[0]?.trim() || '',
                                    },
                                })
                            }
                            sx={fieldSx}
                        />
                    </>
                )}
                <Typography sx={{ fontSize: 12, color: t.form_label, mt: 1.5, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                    Where you'd ideally like your creators to be located. We'll prioritize showing your campaign to
                    creators from here. This will default to your current location if left empty.
                </Typography>
            </Box>
            <WorldwideCampaignBlock
                checked={Boolean(form.world_wide)}
                onChange={v => formDispatch({ type: 'FIELD', field: 'world_wide', value: v })}
            />
            <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                <Typography sx={{ ...labelSx, mb: 2 }}>Content category</Typography>
                <Typography
                    sx={{
                        fontSize: 12,
                        color: t.form_label,
                        mb: 2,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        lineHeight: 1.55,
                    }}>
                    This helps us match your campaign to the right creators. Pick the closest vertical, then get as
                    niche as you can with the sub-category. Shortcuts below are the first {MAIN_CATEGORY_QUICK_PICK} for
                    quick access.
                </Typography>
                <Typography
                    sx={{
                        fontSize: 11,
                        color: t.form_label,
                        mb: 1,
                        textTransform: 'uppercase',
                        letterSpacing: '0.12em',
                    }}>
                    Search all categories (A–Z)
                </Typography>
                <Autocomplete
                    options={nicheOptionsAlphabetical}
                    getOptionLabel={o => o.label}
                    value={nicheOptionsAlphabetical.find(o => o.key === category) ?? null}
                    onChange={(_, opt) => opt && onSelectCategory(opt.key)}
                    isOptionEqualToValue={(a, b) => a.key === b.key}
                    clearOnBlur={false}
                    selectOnFocus
                    handleHomeEndKeys
                    sx={{
                        mb: 2,
                        '& .MuiAutocomplete-popupIndicator': { color: t.form_label },
                        '& .MuiInputBase-input': {
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 15,
                            color: t.primary_white,
                        },
                    }}
                    slotProps={{
                        paper: {
                            sx: {
                                bgcolor: '#1a1a1a',
                                color: t.primary_white,
                                '& .MuiAutocomplete-option.Mui-focused': { bgcolor: 'rgba(255,201,101,0.12)' },
                            },
                        },
                    }}
                    renderInput={params => {
                        const sel = nicheOptionsAlphabetical.find(o => o.key === category);
                        const SelIcon = sel?.Icon;
                        return (
                            <TextField
                                {...params}
                                placeholder="Type to search categories…"
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: SelIcon ? (
                                        <>
                                            <InputAdornment position="start" sx={{ ml: 0.5 }}>
                                                <SelIcon sx={{ fontSize: 22, color: BRAND_LUXE_PRIMARY }} />
                                            </InputAdornment>
                                            {params.InputProps.startAdornment}
                                        </>
                                    ) : (
                                        params.InputProps.startAdornment
                                    ),
                                }}
                                sx={{
                                    ...fieldSx,
                                    '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], py: 0.5 },
                                }}
                            />
                        );
                    }}
                />
                <Typography
                    sx={{
                        fontSize: 11,
                        color: t.form_label,
                        mb: 1,
                        textTransform: 'uppercase',
                        letterSpacing: '0.12em',
                    }}>
                    Popular (A–Z)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    {popularOptions.map(({ key, label, Icon }) => {
                        const active = category === key;
                        return (
                            <Button
                                key={key}
                                onClick={() => onSelectCategory(key)}
                                startIcon={<Icon sx={{ fontSize: 22 }} />}
                                sx={{
                                    borderRadius: 999,
                                    px: 2.5,
                                    py: 1.5,
                                    textTransform: 'none',
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 15,
                                    border: 'none',
                                    gap: 1,
                                    bgcolor: active ? BRAND_LUXE_PRIMARY : '#262626',
                                    color: active ? '#5f4200' : t.primary_white,
                                    boxShadow: active ? '0 0 15px rgba(255,201,101,0.2)' : 'none',
                                    '&:hover': {
                                        bgcolor: active ? BRAND_LUXE_PRIMARY : '#2c2c2c',
                                        color: active ? '#5f4200' : BRAND_LUXE_PRIMARY,
                                    },
                                }}>
                                {label}
                            </Button>
                        );
                    })}
                </Box>

                <Typography sx={{ ...labelSx, mt: 2, mb: 1 }}>Sub-category</Typography>
                <Typography sx={{ fontSize: 12, color: t.form_label, mb: 1, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                    {category ? `Options for ${category} — search to filter.` : 'Select a main category first.'}
                </Typography>
                <Autocomplete
                    disabled={!category}
                    value={subCategory || null}
                    onChange={(_, value) => onSubCategoryChange(value || '')}
                    options={subCategoryOptions}
                    isOptionEqualToValue={(a, b) => a === b}
                    sx={{ '& .MuiAutocomplete-popupIndicator': { color: t.form_label } }}
                    slotProps={{
                        paper: {
                            sx: {
                                bgcolor: '#1a1a1a',
                                color: t.primary_white,
                                '& .MuiAutocomplete-option': { fontSize: 14 },
                                '& .MuiAutocomplete-option.Mui-focused': { bgcolor: 'rgba(255,201,101,0.12)' },
                            },
                        },
                    }}
                    renderInput={params => (
                        <TextField
                            {...params}
                            placeholder="Search or pick a sub-category…"
                            sx={{
                                ...fieldSx,
                                '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], py: 0.5 },
                                '& .MuiInputBase-input': {
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 15,
                                    color: t.primary_white,
                                },
                            }}
                        />
                    )}
                />
            </Box>
        </Box>
    );
}

/* ──────────── Step 1: Budget & Targeting ──────────── */

function StepBudgetTargeting({
    form,
    update,
    budgetInput,
    setBudgetInput,
    publishDate,
    setPublishDate,
    participantsInput,
    setParticipantsInput,
    activeTiers,
    campaignPlatforms,
    t,
    fieldSx,
    budgetFieldSx,
    labelSx,
    maxCreators,
    creatorCapLabel,
    billingCopy,
    hostSubscribeUpsell,
    onOpenHostSubscription,
    hasPlan,
    feeBreakdown,
}: {
    form: any;
    update: (f: string, v: any) => void;
    budgetInput: string;
    setBudgetInput: (v: string) => void;
    publishDate: string;
    setPublishDate: (v: string) => void;
    participantsInput: string;
    setParticipantsInput: (v: string) => void;
    activeTiers: { id: string; name: string; reach: string }[];
    campaignPlatforms: { id: string; label: string; enabled: boolean; coming_soon?: boolean }[];
    t: Colors;
    fieldSx: any;
    budgetFieldSx: any;
    labelSx: any;
    maxCreators: number | null;
    creatorCapLabel: string | null;
    billingCopy: string | null;
    hostSubscribeUpsell: {
        extraCreators: number;
        currentRateFormatted: string;
        subscribedRateFormatted: string;
    } | null;
    onOpenHostSubscription: () => void;
    hasPlan: boolean;
    feeBreakdown?: ReturnType<typeof computeCampaignFeeBreakdown> | null;
}) {
    const f = form as Record<string, any>;
    const isCommission = f.campaign_type === 'commission';
    const isAppInstall = Boolean((f.app_install as IAppInstall)?.enabled);
    const paidCampaignFeeBreakdown = feeBreakdown ?? null;
    const isPercentCommission = isCommission && !isAppInstall;
    const minDate = dayjs().add(1, 'day').toISOString();

    const deliveryMaxDate = useMemo(() => {
        if (f.campaign_type === 'sync_upload' && f.sync_publish_date) {
            return dayjs(f.sync_publish_date).subtract(1, 'day').toISOString();
        }
        return undefined;
    }, [f.campaign_type, f.sync_publish_date]);

    useEffect(() => {
        if (!deliveryMaxDate || !(publishDate || f.end_date)) return;
        const end = f.end_date || publishDate;
        if (!dayjs(end).isBefore(dayjs(f.sync_publish_date))) {
            setPublishDate('');
            update('end_date', '');
        }
    }, [deliveryMaxDate, publishDate, setPublishDate, f.sync_publish_date, f.end_date, update]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            {billingCopy && !isPercentCommission ? (
                <Typography
                    sx={{
                        fontSize: 13,
                        color: 'rgba(255,255,255,0.6)',
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        lineHeight: 1.5,
                    }}>
                    {billingCopy}
                </Typography>
            ) : null}
            {isPercentCommission ? (
                <Typography
                    sx={{
                        fontSize: 13,
                        color: 'rgba(255,255,255,0.6)',
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        lineHeight: 1.5,
                    }}>
                    Commission campaigns have no escrow payout budget. Creator seats and dates shape the estimate; rate
                    and display pool are set on the previous step.
                </Typography>
            ) : null}
            {isAppInstall ? (
                <Typography
                    sx={{
                        fontSize: 13,
                        color: 'rgba(255,255,255,0.6)',
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        lineHeight: 1.5,
                    }}>
                    Set a working budget for install / visit payouts. You’ll lock this amount in escrow before publish;
                    it draws down as attributed results clear.
                </Typography>
            ) : null}

            {!isCommission ? (
                <UgcTargetingConfigPanel>
                    <UgcTargetingSection>
                        <UgcTargetingSectionLabel>Reward type</UgcTargetingSectionLabel>
                        <UgcRewardTypeChips
                            value={f.reward}
                            options={REWARD_OPTIONS}
                            onChange={value => {
                                if (value === 'gifted' && !hasPlan) {
                                    onOpenHostSubscription();
                                    return;
                                }
                                update('reward', value);
                            }}
                        />
                    </UgcTargetingSection>

                    {f.campaign_type !== 'direct_hire' && (
                        <UgcTargetingSection>
                            <UgcTargetingSectionLabel>Platforms</UgcTargetingSectionLabel>
                            <UgcPlatformChips
                                platforms={campaignPlatforms}
                                selectedIds={f.platforms || []}
                                onToggle={platformId => {
                                    const cur = f.platforms || [];
                                    const selected = cur.includes(platformId);
                                    update(
                                        'platforms',
                                        selected ? cur.filter((p: string) => p !== platformId) : [...cur, platformId],
                                    );
                                }}
                            />
                        </UgcTargetingSection>
                    )}

                    <UgcTargetingSection>
                        <UgcTargetingSectionLabel>Influencer tier</UgcTargetingSectionLabel>
                        <UgcInfluencerTierCards
                            tiers={activeTiers}
                            selectedTierId={f.influencer_tier}
                            onSelect={tierId => update('influencer_tier', tierId)}
                        />
                    </UgcTargetingSection>
                </UgcTargetingConfigPanel>
            ) : null}

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: isPercentCommission ? '1fr' : '1fr 1fr' },
                    gap: 2,
                }}>
                {!isPercentCommission ? (
                    <UgcFormFieldCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                            <Typography sx={{ ...labelSx, mb: 0 }}>Total budget</Typography>
                            <Tooltip
                                arrow
                                title={
                                    isAppInstall
                                        ? 'Working budget for CPA payouts as installs or visits clear.'
                                        : 'How much you’re willing to spend on this campaign. Check the breakdown below to confirm it covers the number of creators you’ve selected.'
                                }>
                                <InfoOutlinedIcon
                                    sx={{ fontSize: 16, color: 'rgba(255,255,255,0.45)', cursor: 'help' }}
                                />
                            </Tooltip>
                        </Box>
                        <TextField
                            fullWidth
                            type="text"
                            inputMode="decimal"
                            placeholder="12,500"
                            value={budgetInput}
                            onChange={e => setBudgetInput(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Typography sx={{ color: BRAND_LUXE_PRIMARY, fontWeight: 700, fontSize: 20 }}>
                                            $
                                        </Typography>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                ...budgetFieldSx,
                                '& .MuiOutlinedInput-root': {
                                    ...budgetFieldSx['& .MuiOutlinedInput-root'],
                                    pl: 1,
                                },
                                '& .MuiInputBase-input': {
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 20,
                                    color: t.primary_white,
                                },
                            }}
                        />
                    </UgcFormFieldCard>
                ) : null}
                <UgcFormFieldCard>
                    <Typography sx={labelSx}>{isCommission ? 'Creator seats' : 'Number of creators'}</Typography>
                    <Typography
                        sx={{
                            fontSize: 12,
                            color: t.form_label,
                            mb: 1,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            lineHeight: 1.5,
                        }}>
                        {isCommission
                            ? creatorCapLabel
                                ? `${creatorCapLabel} Seats used for the per-creator estimate.`
                                : 'How many influencers can join. Used for the per-seat commission estimate.'
                            : creatorCapLabel
                            ? `${creatorCapLabel} Enter at least 1 to continue.`
                            : 'How many creators you want on this campaign. Enter at least 1 to continue.'}
                    </Typography>
                    <TextField
                        fullWidth
                        type="text"
                        inputMode="numeric"
                        size="small"
                        value={participantsInput}
                        onChange={e => {
                            const raw = e.target.value;
                            if (raw === '') {
                                setParticipantsInput('');
                                return;
                            }
                            const n = parseInt(raw.replace(/[^\d]/g, ''), 10);
                            if (!Number.isFinite(n)) {
                                setParticipantsInput(raw);
                                return;
                            }
                            setParticipantsInput(String(clampCreatorsToPlanLimit(n, maxCreators)));
                        }}
                        placeholder={maxCreators != null ? `1–${maxCreators}` : 'e.g. 10'}
                        error={isOverCreatorPlanLimit(parseInt(participantsInput.trim(), 10) || 0, maxCreators)}
                        helperText={
                            isOverCreatorPlanLimit(parseInt(participantsInput.trim(), 10) || 0, maxCreators)
                                ? creatorCapLabel
                                : undefined
                        }
                        sx={fieldSx}
                    />
                    {maxCreators != null ? (
                        <Button
                            onClick={onOpenHostSubscription}
                            sx={{
                                mt: 1,
                                alignSelf: 'flex-start',
                                textTransform: 'none',
                                color: BRAND_LUXE_PRIMARY,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 12,
                                px: 0,
                            }}>
                            Need more creators? Upgrade plan
                        </Button>
                    ) : null}
                </UgcFormFieldCard>
            </Box>

            {isCommission ? (
                <UgcFormFieldCard>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                        <Typography sx={{ ...labelSx, mb: 0 }}>
                            Base pay per creator{f.recurrent_deliverables ? ' / delivery' : ''}
                        </Typography>
                        <Tooltip
                            arrow
                            title={
                                isPercentCommission
                                    ? f.recurrent_deliverables
                                        ? 'Guaranteed flat payment to each creator per approved delivery round. Funded separately from your commission wallet — not deducted from the display pool.'
                                        : 'Guaranteed flat payment to each creator upon approval. Funded separately from your commission wallet — not deducted from the display pool.'
                                    : f.recurrent_deliverables
                                    ? 'Guaranteed flat payment to each creator per approved delivery round. Drawn from escrow budget.'
                                    : 'Guaranteed flat payment to each creator upon approval. Drawn from escrow budget.'
                            }>
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.45)', cursor: 'help' }} />
                        </Tooltip>
                    </Box>
                    <Typography
                        sx={{
                            fontSize: 12,
                            color: 'rgba(255,255,255,0.5)',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            mb: 1.5,
                            lineHeight: 1.5,
                        }}>
                        {isPercentCommission
                            ? f.recurrent_deliverables
                                ? 'Each creator receives this flat amount per delivery upon approval. Must be locked in escrow before publishing — separate from the commission pool.'
                                : 'Each creator receives this flat amount upon approval. Must be locked in escrow before publishing — separate from the commission pool.'
                            : f.recurrent_deliverables
                            ? 'Each creator receives this amount per delivery upon approval. Leave blank to distribute from the total budget.'
                            : 'Each creator receives this amount upon approval. Leave blank to distribute from the total budget.'}
                    </Typography>
                    {(() => {
                        const basePayUsd =
                            f.base_payment_per_creator_cents != null && f.base_payment_per_creator_cents !== ''
                                ? Number(f.base_payment_per_creator_cents) / 100
                                : null;
                        const minPay = paidCampaignFeeBreakdown?.minimumPerParticipant ?? null;
                        const isBelowMin = basePayUsd != null && minPay != null && basePayUsd < minPay;
                        return (
                            <>
                                <TextField
                                    fullWidth
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0.00"
                                    size="small"
                                    error={isBelowMin}
                                    value={basePayUsd != null ? String(basePayUsd) : ''}
                                    onChange={e => {
                                        const raw = e.target.value;
                                        if (raw === '') {
                                            update('base_payment_per_creator_cents', null);
                                            return;
                                        }
                                        const parsed = parseFloat(raw);
                                        if (!Number.isFinite(parsed) || parsed < 0) return;
                                        update('base_payment_per_creator_cents', Math.round(parsed * 100));
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Typography
                                                    sx={{ color: BRAND_LUXE_PRIMARY, fontWeight: 700, fontSize: 16 }}>
                                                    $
                                                </Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={fieldSx}
                                />
                                {isBelowMin && (
                                    <Typography sx={{ fontSize: 11, color: '#ef4444', mt: 0.75 }}>
                                        Minimum base pay for {paidCampaignFeeBreakdown?.tier.name ?? 'this'} tier is $
                                        {minPay?.toFixed(2)}.
                                    </Typography>
                                )}
                                {isPercentCommission &&
                                    basePayUsd != null &&
                                    basePayUsd > 0 &&
                                    (() => {
                                        const seats = Math.max(
                                            1,
                                            parseInt(participantsInput.trim(), 10) || f.participants_count || 1,
                                        );
                                        const totalObligation = basePayUsd * seats;
                                        return (
                                            <Typography
                                                sx={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', mt: 0.75 }}>
                                                Escrow required: ${basePayUsd.toFixed(2)} × {seats} creator
                                                {seats !== 1 ? 's' : ''} ={' '}
                                                <strong style={{ color: 'rgba(255,255,255,0.75)' }}>
                                                    ${totalObligation.toFixed(2)}
                                                </strong>{' '}
                                                must be locked in escrow before publishing — separate from the
                                                commission pool.
                                            </Typography>
                                        );
                                    })()}
                            </>
                        );
                    })()}
                </UgcFormFieldCard>
            ) : null}

            <UgcFormFieldCard>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                    <Typography sx={{ ...labelSx, mb: 0 }}>Campaign dates</Typography>
                    <Tooltip
                        arrow
                        title="Set the start date, delivery date (when creators must submit by), and end date (when the campaign closes).">
                        <InfoOutlinedIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.45)', cursor: 'help' }} />
                    </Tooltip>
                </Box>
                {f.campaign_type === 'sync_upload' && f.sync_publish_date && (
                    <Typography sx={{ fontSize: 9, color: 'rgba(255,255,255,0.45)', mb: 1 }}>
                        End date must be before the sync publish date (7 days earlier recommended)
                    </Typography>
                )}
                {/* Recurring deliverables toggle */}
                {true && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 1.5,
                            p: 1.5,
                            borderRadius: 1.5,
                            bgcolor: f.recurrent_deliverables ? 'rgba(255,201,101,0.08)' : 'transparent',
                            border: '1px solid',
                            borderColor: f.recurrent_deliverables ? 'rgba(255,201,101,0.25)' : 'rgba(255,255,255,0.07)',
                        }}>
                        <Box>
                            <Typography sx={{ fontSize: 13, fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff' }}>
                                Recurring deliverables
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: BRAND_LUXE_MUTED, mt: 0.25 }}>
                                {f.campaign_type === 'sync_upload' && !f.recurrent_deliverables
                                    ? 'Sync upload is on, so a recurring series stays off. Turn off sync upload first — they cannot both be on.'
                                    : f.recurrent_deliverables
                                    ? isAppInstall
                                        ? 'Multiple delivery rounds, each with its own deadline and base pay'
                                        : 'Multiple rounds — set deadlines in the schedule below'
                                    : 'Creators submit once against a single delivery date'}
                            </Typography>
                            {f.campaign_type === 'sync_upload' && f.recurrent_deliverables ? (
                                <Typography sx={{ fontSize: 11, color: BRAND_LUXE_PRIMARY, mt: 0.5 }}>
                                    Sync upload and a recurring series cannot both be on. Turn one off before you
                                    launch.
                                </Typography>
                            ) : null}
                        </Box>
                        <Switch
                            checked={Boolean(f.recurrent_deliverables)}
                            disabled={f.campaign_type === 'sync_upload' && !f.recurrent_deliverables}
                            onChange={e => {
                                update('recurrent_deliverables', e.target.checked);
                                if (e.target.checked) {
                                    update('delivery_date', '');
                                }
                            }}
                            sx={{
                                width: 46,
                                height: 28,
                                p: 0,
                                flexShrink: 0,
                                '& .MuiSwitch-switchBase': {
                                    p: '3px',
                                    color: '#adaaaa',
                                    '&.Mui-checked': {
                                        transform: 'translateX(18px)',
                                        color: '#392700',
                                        '& + .MuiSwitch-track': {
                                            bgcolor: BRAND_LUXE_PRIMARY,
                                            opacity: 1,
                                        },
                                    },
                                },
                                '& .MuiSwitch-thumb': {
                                    width: 22,
                                    height: 22,
                                    boxShadow: 'none',
                                },
                                '& .MuiSwitch-track': {
                                    borderRadius: 14,
                                    bgcolor: '#3a3939',
                                    opacity: 1,
                                },
                            }}
                        />
                    </Box>
                )}

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 2,
                    }}>
                    <Box>
                        <Typography
                            sx={{
                                fontSize: 11,
                                color: 'rgba(255,255,255,0.55)',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                mb: 1,
                            }}>
                            Start date
                        </Typography>
                        <CustomDatePicker
                            value={f.start_date || undefined}
                            onChange={val => {
                                update('start_date', val || '');
                                // Clear delivery_date if it no longer satisfies the 7-day rule
                                if (f.delivery_date && val) {
                                    const min = dayjs(val).add(7, 'day');
                                    if (dayjs(f.delivery_date).isBefore(min)) {
                                        update('delivery_date', '');
                                    }
                                }
                            }}
                            minDate={minDate}
                            withTime={false}
                            withTimezone={false}
                            sx={ugcBudgetDatePickerSx}
                        />
                    </Box>
                    <Box>
                        <Typography
                            sx={{
                                fontSize: 11,
                                color: 'rgba(255,255,255,0.55)',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                mb: 1,
                            }}>
                            End date
                        </Typography>
                        <CustomDatePicker
                            value={f.end_date || publishDate || undefined}
                            onChange={val => {
                                const next = val || '';
                                update('end_date', next);
                                setPublishDate(next);
                                // Clear delivery_date if it's no longer before end_date
                                if (f.delivery_date && next && !dayjs(f.delivery_date).isBefore(dayjs(next))) {
                                    update('delivery_date', '');
                                }
                            }}
                            minDate={
                                f.delivery_date && dayjs(f.delivery_date).isValid()
                                    ? dayjs(f.delivery_date).add(1, 'day').toISOString()
                                    : f.start_date && dayjs(f.start_date).isValid()
                                    ? f.start_date
                                    : minDate
                            }
                            maxDate={deliveryMaxDate}
                            withTime={false}
                            withTimezone={false}
                            sx={ugcBudgetDatePickerSx}
                        />
                    </Box>
                </Box>
                {!f.recurrent_deliverables && (
                    <Box sx={{ mt: 2 }}>
                        <Typography
                            sx={{
                                fontSize: 11,
                                color: 'rgba(255,255,255,0.55)',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                mb: 1,
                            }}>
                            Delivery date
                        </Typography>
                        <CustomDatePicker
                            value={f.delivery_date || undefined}
                            onChange={val => update('delivery_date', val || '')}
                            minDate={
                                f.start_date && dayjs(f.start_date).isValid()
                                    ? dayjs(f.start_date).add(7, 'day').toISOString()
                                    : dayjs().add(8, 'day').toISOString()
                            }
                            maxDate={
                                f.end_date && dayjs(f.end_date).isValid()
                                    ? dayjs(f.end_date).subtract(1, 'day').toISOString()
                                    : deliveryMaxDate
                            }
                            withTime={false}
                            withTimezone={false}
                            sx={ugcBudgetDatePickerSx}
                        />
                        <Typography sx={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', mt: 0.5 }}>
                            Creators must submit by this date. Must be 7+ days after start date and before end date.
                        </Typography>
                    </Box>
                )}
                <Box
                    sx={{
                        mt: 2,
                        p: 1.75,
                        borderRadius: '12px',
                        border: '1px solid rgba(216, 195, 173, 0.28)',
                        bgcolor: 'rgba(216, 195, 173, 0.08)',
                    }}>
                    <Typography
                        sx={{
                            fontSize: 13,
                            color: 'rgba(255,255,255,0.78)',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            lineHeight: 1.55,
                        }}>
                        {f.recurrent_deliverables ? (
                            <>
                                The{' '}
                                <Box
                                    component="span"
                                    sx={{ color: BRAND_LUXE_PRIMARY, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                    end date
                                </Box>{' '}
                                is when the campaign closes. Set each round&apos;s submission deadline in the schedule
                                below. No new applications or approvals will be accepted after the end date.
                            </>
                        ) : (
                            <>
                                The{' '}
                                <Box
                                    component="span"
                                    sx={{ color: BRAND_LUXE_PRIMARY, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                    delivery date
                                </Box>{' '}
                                is the creator submission deadline. The{' '}
                                <Box
                                    component="span"
                                    sx={{ color: BRAND_LUXE_PRIMARY, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                    end date
                                </Box>{' '}
                                is when the campaign closes — no new applications or approvals after this date.
                            </>
                        )}
                    </Typography>
                </Box>

                {f.recurrent_deliverables ? (
                    <Box sx={{ mt: 2.5 }}>
                        <Typography sx={{ ...labelSx, mb: 1 }}>Delivery schedule</Typography>
                        {(f.deliverable_rounds || []).length === 0 ? (
                            <Typography
                                sx={{ fontSize: 12, color: '#f87171', mb: 1.5, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                Generate or add at least one delivery with a date and content to launch.
                            </Typography>
                        ) : (f.deliverable_rounds || []).some(
                              (r: any) => !(r.delivery_date && (r.deliverables || []).length > 0),
                          ) ? (
                            <Typography
                                sx={{ fontSize: 12, color: '#E78410', mb: 1.5, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                Every delivery must have a date and at least one content item.
                            </Typography>
                        ) : null}
                        <DeliverableRoundBuilder
                            rounds={f.deliverable_rounds || []}
                            onRoundsChange={rounds => update('deliverable_rounds', rounds)}
                            selectedPlatforms={f.platforms || []}
                            campaignType={f.campaign_type}
                            startDate={f.start_date}
                            endDate={f.end_date}
                        />
                    </Box>
                ) : null}
            </UgcFormFieldCard>

            {!isCommission && hostSubscribeUpsell ? (
                <UgcCampaignHostSubscribeBanner
                    extraCreators={hostSubscribeUpsell.extraCreators}
                    currentRateFormatted={hostSubscribeUpsell.currentRateFormatted}
                    subscribedRateFormatted={hostSubscribeUpsell.subscribedRateFormatted}
                    onSubscribe={onOpenHostSubscription}
                />
            ) : null}
        </Box>
    );
}

/* ──────────── Step 2: Details & Launch ──────────── */

function StepDetailsLaunch({
    form,
    update,
    category,
    subCategory,
    exclusiveRights,
    setExclusiveRights,
    giftOrBoth,
    t,
    fieldSx,
    labelSx,
    assets,
    onAssetsChange,
    sampleVideoFiles,
    onSampleVideoFilesChange,
}: {
    form: any;
    update: (f: string, v: any) => void;
    category: string;
    subCategory: string;
    exclusiveRights: boolean;
    setExclusiveRights: (v: boolean) => void;
    giftOrBoth: boolean;
    t: Colors;
    fieldSx: any;
    labelSx: any;
    assets: AssetEntry[];
    onAssetsChange: (assets: AssetEntry[]) => void;
    sampleVideoFiles: File[];
    onSampleVideoFilesChange: (files: File[]) => void;
}) {
    const f = form as Record<string, any>;
    const isCommission = f.campaign_type === 'commission';
    const isAppInstall = Boolean((f.app_install as IAppInstall)?.enabled);
    const isPercentCommissionWithBasePay = isCommission && !isAppInstall && f.base_payment_per_creator_cents != null;
    const sampleVideoInputRef = useRef<HTMLInputElement>(null);
    const [sampleVideoDragOver, setSampleVideoDragOver] = useState(false);
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                <Typography sx={labelSx}>
                    Campaign brief{' '}
                    <Typography component="span" sx={{ color: '#f87171', fontFamily: 'inherit', fontSize: 'inherit' }}>
                        *
                    </Typography>
                </Typography>
                <RichTextEditor
                    value={f.description || ''}
                    onChange={value => update('description', value)}
                    shellVariant="brandDashboard"
                />
            </Box>
            <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                <Typography sx={labelSx}>Campaign objectives</Typography>
                <DraggableFieldList
                    items={f.objectives || []}
                    onItemsChange={items => update('objectives', items)}
                    placeholder="Add objective…"
                />
                {category &&
                    subCategory &&
                    isValidSubForMain(category, subCategory) &&
                    (f.objectives || []).filter((s: string) => String(s || '').trim()).length === 0 && (
                        <Typography
                            sx={{ fontSize: 12, color: t.form_label, mt: 1, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                            Category ({category} — {subCategory}) will be used as the objective line if none are added
                            above.
                        </Typography>
                    )}
            </Box>
            <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                <Typography sx={labelSx}>Requirements</Typography>
                <Typography
                    sx={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', mb: 1.5, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                    These start filled in. Remove any that do not apply, or add your own. If you request a revision, you
                    can only cite the requirements left on this list.
                </Typography>
                <DraggableFieldList
                    items={f.requirements || []}
                    onItemsChange={items => update('requirements', items)}
                    placeholder="Add requirement…"
                />
            </Box>
            <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                <Typography sx={labelSx}>Creative ideas</Typography>
                <Typography
                    sx={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', mb: 1.5, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                    Optional content angles or hooks for creators — shown as inspiration on the campaign page.
                </Typography>
                <DraggableFieldList
                    items={f.ideas || []}
                    onItemsChange={items => update('ideas', items)}
                    placeholder="Add creative idea…"
                />
            </Box>
            {true ? (
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                    <Typography sx={labelSx}>
                        {f.recurrent_deliverables ? 'Delivery content' : 'Deliverables'}{' '}
                        {(!isCommission || f.recurrent_deliverables || isPercentCommissionWithBasePay) && (
                            <Typography
                                component="span"
                                sx={{ color: '#f87171', fontFamily: 'inherit', fontSize: 'inherit' }}>
                                *
                            </Typography>
                        )}
                        {isCommission && !f.recurrent_deliverables && !isPercentCommissionWithBasePay && (
                            <Typography
                                component="span"
                                sx={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'inherit', fontSize: 12 }}>
                                {' '}
                                optional
                            </Typography>
                        )}
                    </Typography>

                    {f.recurrent_deliverables ? (
                        <Typography
                            sx={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                            Round dates and deliverables are set under Campaign dates above.
                        </Typography>
                    ) : (
                        <>
                            {(f.deliverables || []).filter(
                                (d: any) =>
                                    d?.type &&
                                    (f.campaign_type === 'direct_hire' ||
                                        f.campaign_type === 'commission' ||
                                        d?.platform),
                            ).length === 0 && isPercentCommissionWithBasePay ? (
                                <Typography
                                    sx={{ fontSize: 12, color: '#f87171', mb: 1.5, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                    Add at least one deliverable so creators know what content to submit for base pay.
                                </Typography>
                            ) : (f.deliverables || []).filter(
                                  (d: any) =>
                                      d?.type &&
                                      (f.campaign_type === 'direct_hire' ||
                                          f.campaign_type === 'commission' ||
                                          d?.platform),
                              ).length === 0 && !isCommission ? (
                                <Typography
                                    sx={{ fontSize: 12, color: '#f87171', mb: 1.5, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                    At least one deliverable is required to launch.
                                </Typography>
                            ) : null}
                            <DeliverableList
                                items={f.deliverables || []}
                                onItemsChange={items => update('deliverables', items)}
                                selectedPlatforms={f.platforms || []}
                                isDirectHire={f.campaign_type === 'direct_hire'}
                                campaignType={
                                    f.campaign_type === 'commission' ||
                                    f.campaign_type === 'direct_hire' ||
                                    f.campaign_type === 'sync_upload' ||
                                    f.campaign_type === 'standard'
                                        ? f.campaign_type
                                        : undefined
                                }
                            />
                        </>
                    )}
                </Box>
            ) : null}
            {giftOrBoth && (
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                    <Typography sx={labelSx}>Products / services</Typography>
                    <DraggableFieldList
                        items={f.products || []}
                        onItemsChange={items => update('products', items)}
                        placeholder="Add product or service…"
                    />
                </Box>
            )}
            {f.campaign_type !== 'commission' ? (
                <Box
                    sx={{
                        bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                        p: 3,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                    }}>
                    <Box>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: t.primary_white }}>
                            Exclusive rights
                        </Typography>
                        <Typography
                            sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14, color: t.primary_white, mt: 0.5 }}>
                            Full commercial usage for 12 months
                        </Typography>
                    </Box>
                    <Switch
                        size="small"
                        checked={exclusiveRights}
                        onChange={(_, v) => setExclusiveRights(v)}
                        sx={{
                            flexShrink: 0,
                            '& .MuiSwitch-switchBase.Mui-checked': {
                                color: BRAND_LUXE_SECONDARY,
                                '& + .MuiSwitch-track': { backgroundColor: SECONDARY_CONTAINER, opacity: 1 },
                            },
                            '& .MuiSwitch-track': { borderRadius: 11, opacity: 1, backgroundColor: '#484847' },
                        }}
                    />
                </Box>
            ) : null}
            {f.campaign_type !== 'commission' && f.reward === 'gifted' ? (
                <Box
                    sx={{
                        bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                        p: 3,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                    }}>
                    <Box>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 15, color: t.primary_white }}>
                            Physical product
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: t.primary_white }}>
                            Ship or deliver a physical item to creators
                        </Typography>
                    </Box>
                    <Switch
                        size="small"
                        checked={Boolean(f.physical_product)}
                        onChange={(_, v) => update('physical_product', v)}
                        inputProps={{ 'aria-label': 'Physical product' }}
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                                color: BRAND_LUXE_PRIMARY,
                                '& + .MuiSwitch-track': { backgroundColor: 'rgba(255,201,101,0.5)', opacity: 1 },
                            },
                            '& .MuiSwitch-track': { opacity: 1, backgroundColor: '#484847' },
                        }}
                    />
                </Box>
            ) : null}

            {/* Campaign Assets */}
            <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                <Typography
                    sx={{
                        fontSize: 11,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        textTransform: 'uppercase',
                        letterSpacing: '0.14em',
                        color: t.primary_white,
                        mb: 1.5,
                    }}>
                    Campaign assets
                </Typography>
                <Typography
                    sx={{
                        fontSize: 13,
                        color: 'rgba(255,255,255,0.55)',
                        mb: 2,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        lineHeight: 1.55,
                    }}>
                    Zip all files into one archive and upload here. Only .zip files are supported.
                </Typography>
                <CampaignAssetsEditor assets={assets} onChange={onAssetsChange} />
            </Box>

            {/* Sample Videos */}
            <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                <input
                    ref={sampleVideoInputRef}
                    type="file"
                    multiple
                    hidden
                    accept="video/*"
                    onChange={e => {
                        const picked = Array.from(e.target.files ?? []).filter(f => f.type.startsWith('video/'));
                        onSampleVideoFilesChange([...sampleVideoFiles, ...picked]);
                        e.target.value = '';
                    }}
                />
                <Typography
                    sx={{
                        fontSize: 11,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        textTransform: 'uppercase',
                        letterSpacing: '0.14em',
                        color: t.primary_white,
                        mb: 0.5,
                    }}>
                    Sample videos
                </Typography>
                <Typography
                    sx={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', mb: 2, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                    Show creators what you have in mind. Upload reference videos — they'll be visible to creators
                    browsing your campaign.
                </Typography>

                {/* Drop zone */}
                <Box
                    onDragOver={e => {
                        e.preventDefault();
                        setSampleVideoDragOver(true);
                    }}
                    onDragLeave={() => setSampleVideoDragOver(false)}
                    onDrop={e => {
                        e.preventDefault();
                        setSampleVideoDragOver(false);
                        const dropped = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('video/'));
                        onSampleVideoFilesChange([...sampleVideoFiles, ...dropped]);
                    }}
                    onClick={() => sampleVideoInputRef.current?.click()}
                    sx={{
                        border: `2px dashed ${sampleVideoDragOver ? BRAND_LUXE_PRIMARY : 'rgba(255,255,255,0.12)'}`,
                        borderRadius: 2,
                        py: sampleVideoFiles.length > 0 ? 2 : 4,
                        px: 3,
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s, background 0.2s',
                        bgcolor: sampleVideoDragOver ? 'rgba(255,201,101,0.04)' : 'transparent',
                        '&:hover': { borderColor: BRAND_LUXE_PRIMARY, bgcolor: 'rgba(255,201,101,0.04)' },
                        mb: sampleVideoFiles.length > 0 ? 2 : 0,
                    }}>
                    {sampleVideoFiles.length === 0 ? (
                        <>
                            <Typography
                                sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14, color: t.primary_white, mb: 0.5 }}>
                                Drag & drop videos here
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>
                                MP4, MOV, WEBM · click to browse
                            </Typography>
                        </>
                    ) : (
                        <Typography sx={{ fontSize: 12, color: BRAND_LUXE_PRIMARY, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                            + Add more videos
                        </Typography>
                    )}
                </Box>

                {/* File list */}
                {sampleVideoFiles.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {sampleVideoFiles.map((file, i) => (
                            <Box
                                key={`${file.name}-${i}`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    px: 1.5,
                                    py: 1,
                                    bgcolor: '#1a1a1a',
                                    borderRadius: 1.5,
                                    border: '1px solid rgba(255,255,255,0.06)',
                                }}>
                                <Box sx={{ fontSize: 18 }}>🎬</Box>
                                <Typography
                                    sx={{
                                        flex: 1,
                                        fontSize: 12,
                                        color: t.primary_white,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}>
                                    {file.name}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', flexShrink: 0 }}>
                                    {(file.size / (1024 * 1024)).toFixed(1)} MB
                                </Typography>
                                <Button
                                    type="button"
                                    onClick={e => {
                                        e.stopPropagation();
                                        onSampleVideoFilesChange(sampleVideoFiles.filter((_, idx) => idx !== i));
                                    }}
                                    sx={{
                                        minWidth: 0,
                                        p: 0.5,
                                        color: 'rgba(255,255,255,0.35)',
                                        fontSize: 14,
                                        lineHeight: 1,
                                        '&:hover': { color: '#f87171' },
                                    }}>
                                    ✕
                                </Button>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
}
